import React, {
  Fragment,
  useRef,
  useState,
  useContext,
  createContext,
  useEffect,
  useMemo,
} from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import CodeEditor from "@uiw/react-textarea-code-editor";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import parse from 'html-react-parser';
import Select from "react-select";
import { DateTime } from "luxon";
import {
  Row,
  Col,
  Card,
  Table,
  Modal,
  Badge,
  Dropdown,
  ProgressBar,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Papa from "papaparse";
import loadingCircleImg from "../../../../images/misc/loading-circle-green.svg";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { styled } from "@mui/material/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { utils } from "ethers";
import { MaterialReactTable } from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import { ExportJsonCsv } from "react-export-json-csv";
import { Box, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { FileCopyOutlined as FileCopyOutlinedIcon } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import { darken } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { TypeAnimation } from "react-type-animation";
import {
  areSetsEqual,
  copyTextToClipboard,
  useDocumentTitle,
} from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";

import AddEditCampaign from "./AddEditCampaign";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import CampaignContactUploader from "./CampaignContactUploader";
import useCampaignMemberStatuses from "./useCampaignMemberStatuses";
import BlueLoading from "../../../../images/misc/blue-goo-loading.svg";
import { Parser } from "@json2csv/plainjs";
import {
  addCommas,
  epochToDateString,
  epochToDateTimeString,
  stringToRGB,
  timeAgo,
  toTitleCase,
} from "../../../../utils";

const SSE_STREAM_BASE = "/api/sse/stream"; // e.g. GET /api/sse/stream/:jobId

function safeParse(data) {
  try { return JSON.parse(data); } catch { return data; }
}

function formatLogLine(payload) {
  // payload could be a string or { ts, msg, ... }
  if (typeof payload === "string") return payload;
  const ts = payload.ts ? new Date(payload.ts).toLocaleTimeString() : new Date().toLocaleTimeString();
  const msg = payload.msg ?? JSON.stringify(payload);
  return `[${ts}] ${msg}`;
}

// Sentinel value for the Campaign Members "Comment" dropdown filter, used to
// match members that have no comment set (cannot collide with a real comment).
const COMMENTS_FILTER_NO_COMMENT = "__no_comment__";

const StatusOptions = [
  "Unsent",
  "To be sent",
  "Invited",
  "Declined",
  "Politely Declined",
  "Attending",
  "Tentative",
  "Regrets",
  "Declined, Booked TAP Call",
  "Gave referral",
  "Remove/Unsubscribe from Brand",
  "Remove/Unsubscribe From All",
  "Hold",
]

const CampaignTypeStatusMap = {
  "BD Client Marketing": "To be sent",
  "Client Acquisition Marketing": "To be sent",
  "Client Portfolio Marketing": "To be sent",
  "Curate Attendees": "Unsent",
  "Event Sponsor Outreach": "Unsent",
  "Landmark Event Attendee Marketing": "Unsent",
  "Learning Journey Delegate Recruitment": "Unsent",
  "Summit Event Attendee Marketing": "Unsent",
  "TAP Marketing": "To be sent",
  "Virtual Event Outreach": "Unsent",
};

function getRecommendedStatusForCampaignType(campaignType) {
  return CampaignTypeStatusMap[campaignType] || "Unsent";
}

// Maps a CampaignMember status to the totals-panel colour class. Covers both
// Events (Invited/Attending/...) and BD (Sent/Interested...) statuses; unknown
// statuses fall back to a neutral style.
function getStatusTotalColorClass(statusLabel = "") {
  const normalized = String(statusLabel || "").trim().toLowerCase();
  if (normalized === "invited" || normalized === "sent") return "col-pending";
  if (normalized === "attending" || normalized === "interested, booked") return "col-approved";
  if (normalized === "tentative") return "col-tentative";
  if (
    normalized === "declined" ||
    normalized === "politely declined" ||
    normalized === "regrets" ||
    normalized.startsWith("remove")
  ) {
    return "col-not-attending";
  }
  return "";
}

const ContactListSource = Object.freeze({
  SALESFORCE: "Salesforce",
  APOLLO: "Apollo",
  SEAMLESS: "Seamless",
  ZOOMINFO: "ZoomInfo",
});

// Salesforce org timezone for Last_Sent__c display/write.
const SF_LAST_SENT_TIMEZONE = "America/New_York";
const LAST_SENT_DATE_FORMAT = "MM/dd/yyyy h:mm aa '(ET)'";

function toEtDatetimeLocalValue(date = new Date()) {
  const jsDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(jsDate.getTime())) return "";
  const dt = DateTime.fromJSDate(jsDate, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return "";
  return dt.toFormat("yyyy-MM-dd'T'HH:mm");
}

// Treat a datetime-local string as Eastern Time wall-clock, then emit UTC ISO
// for the Salesforce API so SF shows the same clock time the user picked.
function etDatetimeLocalToIso(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const dt = DateTime.fromISO(raw, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return "";
  return dt.toUTC().toISO();
}

// DatePicker shows browser-local Date components; we feed it a Date whose local
// wall-clock matches the ET value so the picker displays ET numbers.
function etDatetimeLocalToPickerDate(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const dt = DateTime.fromISO(raw, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return null;
  return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, 0, 0);
}

function pickerDateToEtDatetimeLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function buildSalesforceUserOptions(users = []) {
  return (Array.isArray(users) ? users : [])
    .map((user) => {
      const id = String(user?.id || user?.Id || "").trim();
      const name = String(user?.name || user?.Name || "").trim();
      const email = String(user?.email || user?.Email || "")
        .trim()
        .toLowerCase();
      if (!id || !name || !email) return null;
      if (!email.endsWith("@landmarkventures.com")) return null;
      return {
        value: id,
        label: `${name} (${email})`,
        email,
        name,
      };
    })
    .filter(Boolean);
}

function findDefaultSalesforceUserOption(users = [], employeeEmail = "") {
  const email = String(employeeEmail || "").trim().toLowerCase();
  if (!email) return null;
  return (
    buildSalesforceUserOptions(users).find((option) => option.email === email) ||
    null
  );
}

const salesforceUserSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderColor: "#0072ff",
    borderWidth: 1,
    borderStyle: "solid",
    boxShadow: "none",
    borderRadius: "0.375rem",
    backgroundColor: "#ffffff",
    fontSize: 12,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    cursor: "pointer",
    "&:hover": {
      borderColor: "#0072ff",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 32,
    padding: "0 10px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 12,
    color: "#000000",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#8ca1bf",
    fontSize: 12,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 12,
    color: "#000000",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 6,
    color: "#6c757d",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6,
    color: "#6c757d",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    fontSize: 12,
    borderRadius: "0.375rem",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 12,
    backgroundColor: state.isFocused ? "#f0f4fa" : base.backgroundColor,
    color: "#000000",
  }),
};

function normalizeCsvHeader(header = "") {
  return String(header || "")
    .replace(/^\uFEFF/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/_\d+$/, "");
}

function detectContactListSourceFromHeaders(headers = []) {
  const normalizedHeaders = (Array.isArray(headers) ? headers : [])
    .map((header) => normalizeCsvHeader(header))
    .filter(Boolean);
  if (!normalizedHeaders.length) return "";

  const firstHeader = normalizedHeaders[0];
  if (firstHeader === "research date") return ContactListSource.SEAMLESS;

  const hasZoomInfoIndicator = normalizedHeaders.some(
    (header) =>
      header === "zoominfo contact id" ||
      header === "zoominfo company id" ||
      header === "zoominfo contact profile url" ||
      header === "zoominfo company profile url"
  );
  if (hasZoomInfoIndicator) return ContactListSource.ZOOMINFO;

  const hasSalesforceContactId = normalizedHeaders.some(
    (header) => header === "contact id" || header === "contact id (long)"
  );
  const hasBusinessEmail = normalizedHeaders.includes("business email");
  if (hasSalesforceContactId || hasBusinessEmail) {
    return ContactListSource.SALESFORCE;
  }

  const hasApolloIndicator = normalizedHeaders.some(
    (header) =>
      header === "apollo contact id" ||
      header === "apollo account id" ||
      header === "salesforce contact id"
  );
  if (hasApolloIndicator) return ContactListSource.APOLLO;

  return "";
}

const StatusEnum = Object.freeze({
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
  WAITLISTED: "waitlisted",
});

function reorderStatusEnum(
  order = ["APPROVED", "WAITLISTED", "DENIED", "PENDING"]
) {
  const reorderedEnum = {};
  for (const status of order) {
    if (status in StatusEnum) {
      reorderedEnum[status] = StatusEnum[status];
    }
  }
  return reorderedEnum;
}

// Define the possible values for the 'attendanceStatus' enum
const AttendanceStatusEnum = Object.freeze({
  ATTENDING: "attending",
  TENTATIVE: "tentative",
  NOT_ATTENDING: "not_attending",
});

const getAttendanceStatusDisplayText = (attendanceStatus) => {
  let badgeText = "";
  if (attendanceStatus === AttendanceStatusEnum.ATTENDING) {
    badgeText = "Yes";
  } else if (attendanceStatus === AttendanceStatusEnum.TENTATIVE) {
    badgeText = "Tentative";
  } else if (attendanceStatus === AttendanceStatusEnum.NOT_ATTENDING) {
    badgeText = "No";
  }
  return badgeText;
};

const getBadgeBgFromStatus = (status) => {
  let badgeBg = "light";
  if (status === StatusEnum.PENDING) {
    badgeBg = "light";
  } else if (status === StatusEnum.APPROVED) {
    badgeBg = "success";
  } else if (status === StatusEnum.DENIED) {
    badgeBg = "danger";
  } else if (status === StatusEnum.WAITLISTED) {
    badgeBg = "warning";
  }
  return badgeBg;
};

const getBadgeClassFromAttendanceStatus = (attendanceStatus) => {
  let badgeClass = "badge-outline-light";
  if (attendanceStatus === AttendanceStatusEnum.ATTENDING) {
    badgeClass = "badge-outline-success";
  } else if (attendanceStatus === AttendanceStatusEnum.TENTATIVE) {
    badgeClass = "badge-outline-warning";
  } else if (attendanceStatus === AttendanceStatusEnum.NOT_ATTENDING) {
    badgeClass = "badge-outline-danger";
  }
  return badgeClass;
};

function mapEmails(arr, emailKey, setEmailKey = null) {
  const emailsSet = new Set();

  arr.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      if (
        typeof obj[key] === "string" &&
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,7}\b/.test(obj[key])
      ) {
        const email = obj[key].toLowerCase().trim();
        emailsSet.add(email);
        if (setEmailKey && emailKey !== key) {
          setEmailKey(key);
        }
      }
    });
  });

  return Array.from(emailsSet);
}

const ContactStatusUpdatePopup = ({
  attendanceMode = false,
  showPopup,
  setShowPopup,
  contact,
  optionRequest = {},
  position,
  handleUpdateContact,
}) => {
  const [final, setFinal] = useState(optionRequest.final || false);
  const { attendanceStatus, attendanceStatusUpdates } = contact;
  const popupRef = useRef(null);

  useEffect(() => {
    if (showPopup && popupRef.current) {
      // Measure the width of the popup and calculate its left position to center it under the badge.
      const popupWidth = popupRef.current.offsetWidth;
      const popupX = position.x - popupWidth / 2;
      popupRef.current.style.left = `${popupX}px`;
      popupRef.current.style.transform = "scale(0)";

      setTimeout(() => {
        popupRef.current.style.transform = "scale(1)";
      }, 100);
    } else if (popupRef.current) {
      popupRef.current.style.transform = "scale(0)";
    }
  }, [showPopup]);

  const handleBadgeClick = (newStatus) => {
    handleUpdateContact(newStatus, final);
  };

  const handleFinalChange = (event) => {
    setFinal(event.target.checked);
    handleUpdateContact(optionRequest.status, event.target.checked);
  };

  // option updates
  const optionUpdateHistory = { status: null, final: null };
  if (!attendanceMode && optionRequest && optionRequest.statusUpdates) {
    // reverse list so most recent updates are at the start
    const statusUpdates = [...optionRequest.statusUpdates].reverse();
    // marked as final so find most recent final mark
    if (optionRequest.final) {
      let lastFinalUpdate = null;
      // look for a status request which has final: true and remember it, then look for a final: false, when a final: false is found, break
      for (let i = 0; i < statusUpdates.length; i++) {
        const update = statusUpdates[i];
        if (update.final) {
          lastFinalUpdate = update;
        } else if (!update.final) {
          break;
        }
      }
      optionUpdateHistory.final = lastFinalUpdate;
    }
    // get last status change
    let lastStatusUpdate = null;
    for (let i = 0; i < statusUpdates.length; i++) {
      const update = statusUpdates[i];
      if (update.status === optionRequest.status) {
        lastStatusUpdate = update;
      } else if (update.status !== optionRequest.status) {
        break;
      }
    }
    optionUpdateHistory.status = lastStatusUpdate;
  }

  // attendance updates
  const attendanceUpdateHistory = { status: null };
  if (
    attendanceMode &&
    attendanceStatusUpdates &&
    attendanceStatusUpdates.length
  ) {
    // reverse list so most recent updates are at the start
    const statusUpdates = [...attendanceStatusUpdates].reverse();
    // get last status change
    let lastStatusUpdate = null;
    for (let i = 0; i < statusUpdates.length; i++) {
      const update = statusUpdates[i];
      if (update.attendanceStatus === attendanceStatus) {
        lastStatusUpdate = update;
      } else if (update.attendanceStatus !== attendanceStatus) {
        break;
      }
    }
    attendanceUpdateHistory.status = lastStatusUpdate;
  }

  const AttendanceUpdater = () => {
    return (
      <>
        <div className="badge-options">
          {Object.entries(AttendanceStatusEnum).map(([key, status]) => {
            if (status === attendanceStatus) {
              return null; // option already selected
            }
            console.log(`status`, status);
            return (
              <div className="badge-wrapper" key={key}>
                <Badge
                  bg={null}
                  className={`status-badge hvr-shrink ${getBadgeClassFromAttendanceStatus(
                    status
                  )}`}
                  onClick={() => handleBadgeClick(status)}
                >
                  {getAttendanceStatusDisplayText(status)}
                </Badge>
              </div>
            );
          })}
        </div>
        {attendanceUpdateHistory.status ? (
          <div className="option-update-history rmono">
            {attendanceUpdateHistory.status && (
              <>
                <div className="update-line">
                  <span className="">
                    {getAttendanceStatusDisplayText(
                      attendanceUpdateHistory.status.attendanceStatus
                    )}
                  </span>{" "}
                  by {attendanceUpdateHistory.status.employeeName}
                </div>
                <div className="update-line-time rmono">
                  {epochToDateTimeString(
                    attendanceUpdateHistory.status.updateTime
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </>
    );
  };

  const OptionRequestUpdater = () => {
    return (
      <>
        <div className="checkbox-wrapper d-flex justify-content-center align-items-center">
          <label className="d-flex justify-content-center align-items-center">
            <input
              type="checkbox"
              checked={final}
              onChange={handleFinalChange}
              style={{ marginRight: `4px` }}
            />
            Mark as Final
          </label>
        </div>
        <div className="badge-options">
          {Object.entries(StatusEnum).map(([key, status]) => {
            if (status === optionRequest.status) {
              return null; // option already selected
            }
            return (
              <div className="badge-wrapper" key={key}>
                <Badge
                  bg={getBadgeBgFromStatus(status)}
                  className="status-badge hvr-shrink"
                  onClick={() => handleBadgeClick(status)}
                >
                  {toTitleCase(status)}
                </Badge>
              </div>
            );
          })}
        </div>
        {optionUpdateHistory.status || optionUpdateHistory.final ? (
          <div className="option-update-history rmono">
            {optionUpdateHistory.final && (
              <>
                <div className="update-line">
                  <span className="">Final</span> by{" "}
                  {optionUpdateHistory.final.employeeName}
                </div>
                <div className="update-line-time rmono">
                  {epochToDateTimeString(optionUpdateHistory.final.updateTime)}
                </div>
              </>
            )}
            {optionUpdateHistory.status && (
              <>
                <div className="update-line">
                  <span className="">
                    {toTitleCase(optionUpdateHistory.status.status)}
                  </span>{" "}
                  by {optionUpdateHistory.status.employeeName}
                </div>
                <div className="update-line-time rmono">
                  {epochToDateTimeString(optionUpdateHistory.status.updateTime)}
                </div>
              </>
            )}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <>
      <div
        ref={popupRef}
        className="popup-container"
        style={{
          top: position.y + 10,
          left: position.x,
        }}
        onMouseLeave={() => {
          if (popupRef.current) {
            popupRef.current.style.transform = "scale(0)";
          }
          setTimeout(() => {
            setShowPopup(false);
          }, 150);
        }}
      >
        {attendanceMode && <AttendanceUpdater />}
        {!attendanceMode && <OptionRequestUpdater />}
      </div>
    </>
  );
};

const FilterInput = ({ filterKey, placeholder, handleSetFilter, filters }) => {
  const value = filters[filterKey] ? filters[filterKey] : "";
  return (
    <input
      onChange={(e) => handleSetFilter(filterKey, e.target.value)}
      type="text"
      className="form-control mt-1 filter-input"
      placeholder={placeholder}
      value={value}
    ></input>
  );
};

const FilterOption = ({
  filterKey,
  title,
  options,       // ← preferred: array of strings, e.g. ["Unsent","Sent","Responded"]
  optionsEnum,   // ← backward-compat: old object enum { UNSENT: "Unsent", ... }
  handleSetFilter,
  filters = {},
}) => {
  const value = filters?.[filterKey] ?? "All";

  // Normalize to an array of strings.
  const optionList = Array.isArray(options)
    ? options
    : Array.isArray(optionsEnum)
    ? optionsEnum
    : Object.values(optionsEnum || []);

  return (
    <div className="filter-option">
      <div className="input-group">
        <span className="input-group-text">
          <div className="option-title">{title}: </div>
        </span>

        <select
          className="form-control rmono"
          value={value}
          onChange={(e) => handleSetFilter(filterKey, e.target.value)}
        >
          <option key="all" value="All">All</option>
          {optionList.map((status) => {
            const val = String(status);
            return (
              <option key={val} value={val}>
                {toTitleCase ? toTitleCase(val) : val}
              </option>
            );
          })}
        </select>
      </div>
    </div>
  );
};

const FilterCheckbox = ({ title, checked, handleChecked }) => {
  return (
    <div className="filter-option">
      <div className="input-group">
        <span className="input-group-text">
          <div className="option-title">{title}</div>
        </span>
        <div
          className="form-check custom-checkbox checkbox-success check-lg bs_exam_topper"
          // style={{ marginLeft: "4px" }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            id={`FilterCheckbox_${title}`}
            required
            checked={checked}
            onChange={(event) => handleChecked(event)}
          />
        </div>
      </div>
    </div>
  );
};

const ConfirmEmailTemplateEditor = ({ template, getTemplates, employee }) => {
  const [title, setTitle] = useState(template ? template.title : "");
  const [emailSubject, setEmailSubject] = useState(
    template ? template.emailSubject : ""
  );
  const [emailBody, setEmailBody] = useState(
    template ? template.emailBody : ""
  );
  const [emailSendFrom, setEmailSendFrom] = useState(
    template ? template.emailSendFrom : ""
  );
  const [emailSendFromName, setEmailSendFromName] = useState(
    template ? template.emailSendFromName : ""
  );

  const [sendFromOptions, setSendFromOptions] = useState([]);

  useEffect(() => {
    // new option created
    if (!template) {
      setTitle("");
      setEmailSubject("");
      setEmailBody("");
      setEmailSendFrom("");
      setEmailSendFromName("");
    }
    // existing option obj
    else {
      setTitle(template.title);
      setEmailSubject(template.emailSubject);
      setEmailBody(template.emailBody);
      setEmailSendFrom(template.emailSendFrom);
      setEmailSendFromName(template.emailSendFromName);
    }
  }, [template]);

  async function getSendFromOptions() {
    try {
      const api = new ApiService({ employee });
      const response = await api.get(
        `landmark/emailTemplates/getSendFromOptions`
      );
      console.log(`sendFromOptions`, response);
      setSendFromOptions(response);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getSendFromOptions();
  }, []);

  const emailHtml = `${emailBody}`;
  //.replace(`{firstName}`)}`;

  // const handleSetOptions = (optionId, emailHtml) => {
  //   const newOptions = options.map((o) => {
  //     if (o.optionId === optionId) {
  //       o.emailHtml = emailHtml;
  //     }
  //     return o;
  //   });
  //   setOptions(newOptions);
  // };

  async function editTemplateEmail() {
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/emailTemplates/editTemplate", {
        templateId: template.templateId,
        title,
        emailSubject,
        emailBody,
        emailSendFrom,
        emailSendFromName,
      });
      console.log(
        `editTemplateEmail() - landmark/emailTemplates/editTemplate - response`,
        response
      );
      const { success, error } = response;
      if (!success) {
        toast.error("An error occurred");
      } else if (error) {
        toast.error(error);
      } else {
        toast.success(success);
        // refresh templates
        getTemplates();
      }
    } catch (error) {
      console.error(error);
    }
  }

  function handleSetEmailSendFrom(value) {
    const { name } = sendFromOptions.find((o) => o.value === value);
    // update name when new value is selected
    if (value !== emailSendFrom && name) {
      setEmailSendFromName(name);
    }
    setEmailSendFrom(value);
  }

  return (
    <div className="confirm-email-editor-container">
      <div className="title">Email Template Editor</div>
      <div className="row">
        <div className="col-6">
          <div className="subtitle">Edit Template</div>
          <div className="template-form">
            <div className="row">
              <div className="col-12">
                <button
                  className={`create-event-btn save-changes-btn btn btn-sm btn-primary`}
                  onClick={editTemplateEmail}
                >
                  {`Save Changes`}
                  <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
                </button>
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Template Title
                  {/* <span className="example-text">e.g. Andrew Kalaigian</span> */}
                </label>
                <input
                  type="text"
                  value={title}
                  className="form-control"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Send From Email <br></br>
                  <span className="example-text" style={{ fontSize: "12px" }}>
                    (Contact <i>mjones@landmarkventures.com</i> to configure new
                    emails)
                  </span>
                </label>
                <select
                  className="form-control lh-1"
                  onChange={(e) => handleSetEmailSendFrom(e.target.value)}
                  value={emailSendFrom}
                >
                  {sendFromOptions.map((item, key) => (
                    <option key={`sf_option_${key}`} value={item.value}>
                      {item.value}
                    </option>
                  ))}
                </select>
                {/* <input
                  type="text"
                  value={emailSendFrom}
                  className="form-control"
                  onChange={(e) => setEmailSendFrom(e.target.value)}
                /> */}
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Send From Name{" "}
                  {/* <span className="example-text">e.g. Andrew Kalaigian</span> */}
                </label>
                <input
                  type="text"
                  value={emailSendFromName}
                  className="form-control"
                  onChange={(e) => setEmailSendFromName(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Subject <span className="example-text"></span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  className="form-control"
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Body<br></br>{" "}
                  <span className="example-text" style={{ fontSize: "12px" }}>
                    Available variables:<br></br>
                    <i>
                      firstName, lastName, fullName, company, title, department,
                      city, state, country
                    </i>
                  </span>
                  <br></br>{" "}
                  <span className="example-text" style={{ fontSize: "12px" }}>
                    To use a variable simply wrap it in curly braces e.g.
                    {` {`}
                    <i>firstName</i>
                    {`}`}
                  </span>
                  <br></br>{" "}
                  <span className="example-text" style={{ fontSize: "12px" }}>
                    Please note that data other than{" "}
                    <i>firstName, lastName, fullName</i> is not guaranteed to be
                    available for every contact. It's therefore recommended you
                    use these variables sparingly.
                  </span>
                </label>
                {/* <textarea
                  value={emailBody}
                  className="form-control textarea rmono"
                  rows="20"
                  onChange={(e) => setEmailBody(e.target.value)}
                /> */}
                <CodeEditor
                  value={emailBody}
                  language="html"
                  onChange={(evn) => setEmailBody(evn.target.value)}
                  padding={15}
                  data-color-mode="light"
                  style={{
                    color: "#000",
                    fontSize: 11,
                    backgroundColor: "#fff",
                    border: "1px #0072ff solid",
                    borderRadius: "0.625rem",
                    fontFamily:
                      "ui-monospace,Roboto Mono,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="subtitle">Viewer</div>
          <div className="viewer">
            <div className="subject">{emailSubject}</div>
            <div className="divider"></div>
            <div className="body">{parse(emailHtml)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PostContentDisplay = ({ postContent }) => {
  const maxLength = 100;
  const [showFullContent, setShowFullContent] = useState(false);

  const truncatedContent = postContent.slice(0, maxLength);
  const shouldTruncate = postContent.length > maxLength;

  const toggleShowMore = () => {
    setShowFullContent(!showFullContent);
  };

  const showMoreLessButton = (
    <>
      {" "}
      <span
        style={{
          textDecoration: "underline",
          cursor: "pointer",
          color: "blue",
        }}
        onClick={toggleShowMore}
      >
        {showFullContent ? "Show less" : "Show more"}
      </span>
    </>
  );

  return (
    <div>
      <p>
        {showFullContent ? postContent : truncatedContent}
        {shouldTruncate && showMoreLessButton}
      </p>
    </div>
  );
};

const MasterEventsTable = ({
  employee,
  selectedCampaign,
  updateCampaignMemberStatus,
  updateCampaignMemberComment,
  bulkUpdateCampaignMembers,
  startDeleteCampaignMembersJob,
  applyDeletedCampaignMembers,
  campaigns,
  salesforceUsers = [],
  sentEmails,
  getSentEmails,
  emailJobs,
  refreshSelectedCampaign,
  loadingContactsRefresh,
}) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContactEmails, setSelectedContactEmails] = useState([]);
  const [contactConfirmationEmails, setContactConfirmationEmails] = useState(
    []
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [filters, setFilters] = useState({});
  const [filtersEnabled, setFiltersEnabled] = useState(false);
  const [approvedAttendingFilter, setApprovedAttendingFilter] = useState(false);
  const [uncontactedFilter, setUncontactedFilter] = useState(false);
  const [contactedFilter, setContactedFilter] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showDeleteMembersModal, setShowDeleteMembersModal] = useState(false);
  const [deletingSelectedMembers, setDeletingSelectedMembers] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    total: 0,
    processed: 0,
    deletedCount: 0,
    failedCount: 0,
    batchNo: 0,
    totalBatches: 0,
  });
  const [deleteFailureReasons, setDeleteFailureReasons] = useState([]);
  const [deleteRunCompleted, setDeleteRunCompleted] = useState(false);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [savingCommentId, setSavingCommentId] = useState(null);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkComment, setBulkComment] = useState("");
  const [bulkUpdateLastSent, setBulkUpdateLastSent] = useState(true);
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState(false);
  const [bulkUpdatingComment, setBulkUpdatingComment] = useState(false);
  const [bulkEmailVersion, setBulkEmailVersion] = useState("");
  const [bulkUpdatingEmailVersion, setBulkUpdatingEmailVersion] = useState(false);
  const salesforceUserOptions = useMemo(
    () => buildSalesforceUserOptions(salesforceUsers),
    [salesforceUsers]
  );
  const defaultSentAsOption = useMemo(
    () => findDefaultSalesforceUserOption(salesforceUsers, employee?.email),
    [salesforceUsers, employee?.email]
  );
  const [bulkCampaignSentAs, setBulkCampaignSentAs] = useState(null);
  const [bulkUpdatingCampaignSentAs, setBulkUpdatingCampaignSentAs] =
    useState(false);
  const [bulkLastSent, setBulkLastSent] = useState(() => toEtDatetimeLocalValue());
  const [bulkUpdatingLastSentField, setBulkUpdatingLastSentField] =
    useState(false);

  useEffect(() => {
    if (!bulkCampaignSentAs && defaultSentAsOption) {
      setBulkCampaignSentAs(defaultSentAsOption);
    }
  }, [bulkCampaignSentAs, defaultSentAsOption]);
  const deleteEsControllerRef = useRef(null);
  const deleteRetryTimeoutRef = useRef(null);

  // Status options for the selected campaign, loaded from its CampaignMemberStatus
  // list so BD campaigns (To be sent/Sent) and Events campaigns (Unsent/Invited)
  // each show the correct values for the status filter and per-row status inputs.
  const { statusOptions: campaignStatusOptions } = useCampaignMemberStatuses({
    employee,
    salesforceCampaignId: selectedCampaign?.Id,
    recordTypeName: selectedCampaign?.RecordType?.Name,
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") setShiftHeld(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Shift") setShiftHeld(false);
    };
    const handleBlur = () => setShiftHeld(false);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (deleteRetryTimeoutRef.current) {
        clearTimeout(deleteRetryTimeoutRef.current);
        deleteRetryTimeoutRef.current = null;
      }
      if (deleteEsControllerRef.current) {
        deleteEsControllerRef.current.close?.();
        deleteEsControllerRef.current = null;
      }
    };
  }, []);

  const handleStatusChange = (e, campaignMemberId) => {
    if(!e || !e.target) return;
    const status = e.target.value;
    //const selected = (campaigns || []).find(c => c.Id === id) || null;
    console.log(`handleStatusChange - status: ${status}, campaignMemberId: ${campaignMemberId}`);
    if (status && campaignMemberId) {
      updateCampaignMemberStatus(status, campaignMemberId);
    }
  };

  const beginEditComment = (campaignMemberId, currentComment) => {
    setEditingCommentId(campaignMemberId);
    setCommentDraft(String(currentComment || ""));
  };

  const handleSaveComment = async (campaignMemberId, draft, currentComment) => {
    if (String(draft) === String(currentComment || "")) return;
    setSavingCommentId(campaignMemberId);
    try {
      const ok = await updateCampaignMemberComment(draft, campaignMemberId);
      if (ok) setEditingCommentId(null);
    } finally {
      setSavingCommentId(null);
    }
  };

  const bulkStatusIsSentType = ["sent", "invited"].includes(
    String(bulkStatus || "").trim().toLowerCase()
  );

  const handleBulkUpdateStatus = async () => {
    if (!bulkStatus || !selectedMemberIds.length) return;
    setBulkUpdatingStatus(true);
    try {
      const ok = await bulkUpdateCampaignMembers(selectedMemberIds, {
        status: bulkStatus,
        updateLastSent: bulkStatusIsSentType && bulkUpdateLastSent,
      });
      if (ok) setBulkStatus("");
    } finally {
      setBulkUpdatingStatus(false);
    }
  };

  const handleBulkUpdateComment = async () => {
    if (!selectedMemberIds.length) return;
    setBulkUpdatingComment(true);
    try {
      const ok = await bulkUpdateCampaignMembers(selectedMemberIds, {
        comment: bulkComment,
      });
      if (ok) setBulkComment("");
    } finally {
      setBulkUpdatingComment(false);
    }
  };

  const handleBulkUpdateEmailVersion = async () => {
    if (!selectedMemberIds.length) return;
    setBulkUpdatingEmailVersion(true);
    try {
      await bulkUpdateCampaignMembers(selectedMemberIds, {
        emailVersion: bulkEmailVersion,
      });
    } finally {
      setBulkUpdatingEmailVersion(false);
    }
  };

  const handleBulkUpdateCampaignSentAs = async () => {
    if (!selectedMemberIds.length || !bulkCampaignSentAs?.value) return;
    setBulkUpdatingCampaignSentAs(true);
    try {
      await bulkUpdateCampaignMembers(selectedMemberIds, {
        campaignSentAs: bulkCampaignSentAs.value,
      });
    } finally {
      setBulkUpdatingCampaignSentAs(false);
    }
  };

  const handleBulkUpdateLastSentField = async () => {
    if (!selectedMemberIds.length || !bulkLastSent) return;
    const lastSentIso = etDatetimeLocalToIso(bulkLastSent);
    if (!lastSentIso) {
      toast.error("Invalid Last Sent datetime (Eastern Time)");
      return;
    }
    setBulkUpdatingLastSentField(true);
    try {
      await bulkUpdateCampaignMembers(selectedMemberIds, {
        lastSent: lastSentIso,
      });
    } finally {
      setBulkUpdatingLastSentField(false);
    }
  };

  const handleSetUncontactedFilter = (event) => {
    const checked = event.target.checked;
    setUncontactedFilter(checked);
  };

  const handleSetContactedFilter = (event) => {
    const checked = event.target.checked;
    setContactedFilter(checked);
  };

  const handleSetFiltersEnabled = (event) => {
    const checked = event.target.checked;
    setFiltersEnabled(checked);
  };

  const getFilteredContacts = () => {
    function isString(s) {
      return typeof s === "string" || s instanceof String;
    }

    // filter just the contacts for the selected event
    let filtered = contacts || [];

    // bypassed
    if (!filtersEnabled) {
      return filtered;
    }

    for (const filterKey in filters) {
      const filterValue = filters[filterKey];
      // status filtering
      if (filterKey === "status") {
        if (filterValue === "All") {
          // no nothing (allow all)
        } else {
          filtered = filtered.filter(
            (c) =>
              c.status.toLowerCase().trim() === filterValue.toLowerCase().trim()
          );
        }
      }
      // comment filtering (exact match against a chosen comment, or no-comment)
      else if (filterKey === "comments") {
        if (filterValue === "All") {
          // allow all
        } else if (filterValue === COMMENTS_FILTER_NO_COMMENT) {
          filtered = filtered.filter(
            (c) => !String(c.comments || "").trim()
          );
        } else {
          filtered = filtered.filter(
            (c) =>
              String(c.comments || "").trim().toLowerCase() ===
              String(filterValue).trim().toLowerCase()
          );
        }
      }
      // string filtering
      else if (isString(filterValue) && filterValue.length > 0) {
        filtered = filtered.filter((contact) =>
          contact[filterKey].toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    }

    if (uncontactedFilter && contactedFilter) {
      // show all (no added filtering)
    }
    // uncontacted only filter
    else if (uncontactedFilter && !contactedFilter) {
      filtered = filtered.filter((contact) => {
        const hasBeenSentEmail = sentEmails.find(
          (e) => e.contactEmail.toLowerCase() === contact.email.toLowerCase()
        );
        return !hasBeenSentEmail;
      });
    }
    // contacted only filter
    else if (contactedFilter && !uncontactedFilter) {
      filtered = filtered.filter((contact) => {
        const hasBeenSentEmail = sentEmails.find(
          (e) => e.contactEmail.toLowerCase() === contact.email.toLowerCase()
        );
        return hasBeenSentEmail;
      });
    } else {
      // show all (no added filtering)
    }

    return filtered;
  };
  let filteredContacts = getFilteredContacts();

  // Distinct, non-empty comments across the current campaign's members, used to
  // populate the "Comment" dropdown filter (mirrors Sendora's comment filter).
  const commentFilterOptions = useMemo(() => {
    const uniqueByLower = new Map();
    (contacts || []).forEach((c) => {
      const trimmed = String(c?.comments || "").trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (!uniqueByLower.has(key)) uniqueByLower.set(key, trimmed);
    });
    return Array.from(uniqueByLower.values()).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [contacts]);

  // Campaign member ids for the currently selected rows (selection is tracked by
  // email), used as the target set for bulk status/comment updates.
  const selectedMemberIds = useMemo(
    () =>
      (contacts || [])
        .filter((c) => selectedContactEmails.includes(c.email))
        .map((c) => c.id)
        .filter(Boolean),
    [contacts, selectedContactEmails]
  );

  // OptionRequest popup
  const [showPopupOR, setShowPopupOR] = useState(false);
  const [showPopupAttendance, setShowPopupAttendance] = useState(false);
  const [popupPositionOR, setPopupPositionOR] = useState({ x: 0, y: 0 });
  const [popupPositionAttendance, setPopupPositionAttendance] = useState({
    x: 0,
    y: 0,
  });
  const [selectedContactEvent, setSelectedContactEvent] = useState(null);
  const [selectedOptionRequest, setSelectedOptionRequest] = useState(null);
  const [showEditTemplate, setShowEditTemplate] = useState(false);

  useEffect(() => {
    if (
      selectedCampaign &&
      selectedCampaign.members &&
      selectedCampaign.members.length
    ) {
function convertMembersToContacts(members) {
  if (!Array.isArray(members)) return [];

  const getLastPathSegment = (url) => {
    if (typeof url !== "string" || !url) return "";
    try {
      // tolerate relative SF urls too
      const u = new URL(url, "https://example.com");
      const parts = u.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      // fallback if it's not a valid URL
      const parts = url.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    }
  };

  return members
    .filter(Boolean) // drop null/undefined
    .map((member) => {
      const contact = member?.Contact ?? {};
      const account = contact?.Account ?? {};
      const accountUrl = account?.attributes?.url;

      // Name fallback if Contact.Name missing
      const name =
        contact?.Name ??
        [contact?.FirstName, contact?.LastName].filter(Boolean).join(" ") ??
        "";

      return {
        id: member?.Id ?? "",
        contactId: member?.ContactId ?? "",
        status: member?.Status ?? "Unknown",
        comments: String(member?.Comments__c || member?.Comments || "").trim(),
        campaignId: member?.CampaignId ?? "",
        email: contact?.Email ?? "",
        name,
        title: contact?.Title ?? "",
        accountName: account?.Name ?? "",
        accountId: getLastPathSegment(accountUrl),
        linkedIn: contact?.LinkedIn__c ?? "",
        city: contact?.MailingCity ?? "",
        state: contact?.MailingState ?? "",
        country: contact?.MailingCountry ?? "",
      };
    });
}
      const convertedContacts = convertMembersToContacts(
        selectedCampaign.members
      );
      console.log(`setContacts`, convertedContacts);
      setContacts(convertedContacts);
    } else {
      setContacts([]);
    }
  }, [selectedCampaign]);

  const copyText = (text, desc = null) => {
    copyTextToClipboard(text);
    desc = desc ? desc : "text";
    toast.success(`Copied ${desc} to clipboard`);
  };

  let canNextPage = false;
  let canPreviousPage = false;
  const pageCount = Math.ceil(filteredContacts.length / rowsPerPage);
  console.log(`pageIndex`, pageIndex);
  console.log(`pageCount`, pageCount);
  if (pageIndex + 1 < pageCount) {
    canNextPage = true;
  }
  if (pageIndex > 0) {
    canPreviousPage = true;
  }

  const goToPage = (pageNo) => {
    if (pageNo >= 0 && pageNo < pageCount) {
      setPageIndex(pageNo);
    } else {
    }
  };
  const nextPage = () => {
    if (canNextPage) {
      setPageIndex(pageIndex + 1);
    }
  };
  const previousPage = () => {
    if (canPreviousPage) {
      setPageIndex(pageIndex - 1);
    }
  };
  // Calculate the start index and end index for the current page
  const startIndex = pageIndex * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  // get contacts to display on the current page
  const pageContacts = filteredContacts.slice(startIndex, endIndex);

  if (!filteredContacts || !selectedCampaign) {
    return <>No Contacts</>;
  }

  // get selected event obj
  const event = campaigns.find((c) => c.campaignId === selectedCampaignId);

  const handleSubmitContactComment = async (
    contactCampaignId,
    content,
    isReply,
    replyCommentId
  ) => {
    try {
      if (!employee) {
        toast.error(`An error occurred: employee not logged in`);
        return;
      }
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/campaigns/submitEventContactComment`,
        {
          contactCampaignId,
          employee,
          content,
          isReply,
          replyCommentId,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        return true;
      } else if (info) {
        toast(`${info}`);
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleUpdateContactOptionsRequest = async (status, final) => {
    try {
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/campaigns/updateEventContactOptionRequest`,
        {
          contactCampaignId: selectedContactEvent.contactCampaignId,
          optionRequestId: selectedOptionRequest.optionRequestId,
          status,
          final,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleUpdateContactAttendance = async (attendanceStatus) => {
    try {
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/campaigns/updateEventContactAttendanceStatus`,
        {
          contactCampaignId: selectedContactEvent.contactCampaignId,
          attendanceStatus,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const sendContactEmail = async (campaignId, contactEmails = []) => {
    try {
      if (!employee) {
        toast.error(`An error occurred: employee not logged in`);
        return;
      }
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/campaigns/sendEmailToContactForCampaign`,
        {
          campaignId,
          contactEmails,
          employee,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        // refresh emails (now handled by websockets)
        //getSentEmails();
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const selectContacts = (contactsToSelect) => {
    const newSelectedContactEmails = [...selectedContactEmails];
    contactsToSelect.forEach((contact) => {
      if (!newSelectedContactEmails.includes(contact.email)) {
        newSelectedContactEmails.push(contact.email);
      }
    });
    setSelectedContactEmails(newSelectedContactEmails);
  };

  const unselectContacts = (contactsToUnselect) => {
    const contactEmailsToUnselect = contactsToUnselect.map((r) => r.email);
    setSelectedContactEmails((prevSelectedContactEmails) => {
      return prevSelectedContactEmails.filter(
        (r) => !contactEmailsToUnselect.includes(r)
      );
    });
  };

  const toggleSelectAllPageContacts = (event) => {
    console.log(`toggleSelectAllPageContacts() - event.target`, event.target);
    const checked = event.target.checked;
    // select all contacts on this page
    if (checked) {
      selectContacts(pageContacts);
    }
    // unselect all contacts on this page
    else {
      unselectContacts(pageContacts);
    }
  };
  const toggleSelectContact = (event, contact) => {
    const checked = event.target.checked;
    console.log(`toggleSelectContact() - checked`, checked);
    if (checked) {
      selectContacts([contact]);
    } else {
      unselectContacts([contact]);
    }
  };

  if (!selectedCampaign) {
    return null;
  }
  console.log(`selectedCampaign`, selectedCampaign);

  const handleSetFilter = (filterKey, value) => {
    const newFilters = { ...filters };
    newFilters[filterKey] = value;
    setFilters(newFilters);
  };

  let headers = [];
  // Assuming contacts is not an empty array
  if (contacts.length > 0) {
    const headersSet = new Set();
    contacts.forEach((r) => {
      Object.keys(r).forEach((key) => {
        headersSet.add(key);
      });
    });

    headers = Array.from(headersSet).map((h) => {
      return { key: h, name: h };
    });
    // add option headers
    if (selectedCampaign && selectedCampaign.options) {
      selectedCampaign.options.forEach((option) => {
        const { shortTitle, optionId } = option;
        headers.push({ key: optionId, name: shortTitle });
      });
    }
    // remove unwanted fields
    const unwanted = [
      "attendanceStatusUpdates",
      "comments",
      "confirmEmails",
      "optionRequests",
      "__v",
      "_id",
      "partnerResponses",
    ];
    headers = headers.filter((h) => !unwanted.includes(h.key));
    console.log(`headers`, headers);
  }

  const selectAllBtn = () => {
    // unselect all
    unselectContacts(contacts);
    // selected all filtered
    selectContacts(filteredContacts);
    toast.success(`${filteredContacts.length} contacts selected`);
  };

  const unselectAllBtn = () => {
    // unselect all
    unselectContacts(contacts);
  };

  const sendEmailToSelectedContactsBtn = () => {
    sendContactEmail(selectedCampaign.campaignId, selectedContactEmails);
    unselectContacts(contacts);
  };

  const selectedCampaignMemberIds = [
    ...new Set(
      contacts
        .filter((contact) => selectedContactEmails.includes(contact.email))
        .map((contact) => String(contact?.id || "").trim())
        .filter(Boolean)
    ),
  ];

  const openDeleteSelectedMembersModal = () => {
    if (!selectedCampaignMemberIds.length) return;
    setShowDeleteMembersModal(true);
  };

  const closeDeleteSelectedMembersModal = () => {
    if (deletingSelectedMembers) return;
    setShowDeleteMembersModal(false);
    setDeleteRunCompleted(false);
    setDeleteFailureReasons([]);
  };

  const extractAsyncJobId = (response) => {
    if (!response) return null;
    let payload = response;
    if (typeof response === "string") {
      try {
        payload = JSON.parse(response);
      } catch {
        return null;
      }
    }
    const candidates = [
      payload?.jobId,
      payload?.jobID,
      payload?.taskId,
      payload?.taskID,
      payload?.id,
      payload?.data?.jobId,
      payload?.data?.jobID,
      payload?.data?.taskId,
      payload?.data?.taskID,
      payload?.data?.id,
    ];
    return candidates.find((value) => typeof value === "string" && value.trim()) || null;
  };

  const closeDeleteStreamResources = () => {
    if (deleteRetryTimeoutRef.current) {
      clearTimeout(deleteRetryTimeoutRef.current);
      deleteRetryTimeoutRef.current = null;
    }
    if (deleteEsControllerRef.current) {
      deleteEsControllerRef.current.close?.();
      deleteEsControllerRef.current = null;
    }
  };

  const startDeleteMembersStream = (api, jobId, retryAttempt = 0) => {
    if (!jobId) return;
    const MAX_UNKNOWN_JOB_RETRIES = 5;

    closeDeleteStreamResources();
    setDeletingSelectedMembers(true);

    const controller = api.openJobStream(jobId, {
      onLog: (payloadOrString) => {
        const payload =
          typeof payloadOrString === "string"
            ? safeParse(payloadOrString)
            : payloadOrString;
        if (payload && payload.type === "progress") {
          setDeleteProgress((prev) => ({
            ...prev,
            total: Number(payload.total || prev.total || 0),
            processed: Number(payload.processed || 0),
            deletedCount: Number(payload.deletedCount || 0),
            failedCount: Number(payload.failedCount || 0),
            batchNo: Number(payload.batchNo || 0),
            totalBatches: Number(payload.totalBatches || 0),
          }));
        }
      },
      onDone: (payload) => {
        const deletedCampaignMemberIds = Array.isArray(payload?.deletedCampaignMemberIds)
          ? payload.deletedCampaignMemberIds
          : [];
        const failed = Array.isArray(payload?.failed) ? payload.failed : [];
        const failedReasonSummary = Array.isArray(payload?.failedReasonSummary)
          ? payload.failedReasonSummary
          : [];
        const normalizedReasons = failedReasonSummary.length
          ? failedReasonSummary.map((entry) => ({
              reason: String(entry?.reason || "Unknown error"),
              count: Number(entry?.count || 0),
            }))
          : (() => {
              const reasonCount = {};
              failed.forEach((item) => {
                const reason = String(item?.error || "Unknown error");
                reasonCount[reason] = (reasonCount[reason] || 0) + 1;
              });
              return Object.entries(reasonCount)
                .map(([reason, count]) => ({ reason, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10);
            })();
        setDeleteFailureReasons(normalizedReasons);
        setDeleteRunCompleted(true);

        if (deletedCampaignMemberIds.length) {
          const deletedIdSet = new Set(deletedCampaignMemberIds);
          const deletedContactEmails = new Set(
            contacts
              .filter((contact) => deletedIdSet.has(contact.id))
              .map((contact) => contact.email)
          );
          setSelectedContactEmails((prevSelectedEmails) =>
            prevSelectedEmails.filter((email) => !deletedContactEmails.has(email))
          );
          applyDeletedCampaignMembers(deletedCampaignMemberIds);
          toast.success(
            `Deleted ${deletedCampaignMemberIds.length} campaign member${
              deletedCampaignMemberIds.length === 1 ? "" : "s"
            }`
          );
        }

        if (failed.length) {
          console.log("Delete campaign members failed reasons:", normalizedReasons);
          toast.error(
            `Failed to delete ${failed.length} campaign member${
              failed.length === 1 ? "" : "s"
            }`
          );
        }

        closeDeleteStreamResources();
        setDeletingSelectedMembers(false);
        if (!failed.length) {
          setShowDeleteMembersModal(false);
          setDeleteRunCompleted(false);
          setDeleteFailureReasons([]);
        }
      },
      onError: (payload) => {
        const errorMsg = String(payload?.error || "").trim();
        const isUnknownJobIdError =
          errorMsg.toLowerCase() === "unknown jobid" ||
          errorMsg.toLowerCase().includes("unknown jobid");
        if (isUnknownJobIdError && retryAttempt < MAX_UNKNOWN_JOB_RETRIES) {
          const nextAttempt = retryAttempt + 1;
          const retryDelayMs = Math.min(1500 * nextAttempt, 5000);
          deleteRetryTimeoutRef.current = setTimeout(() => {
            startDeleteMembersStream(api, jobId, nextAttempt);
          }, retryDelayMs);
          return;
        }
        closeDeleteStreamResources();
        setDeletingSelectedMembers(false);
        toast.error(payload?.error || "Delete job failed");
      },
    });

    deleteEsControllerRef.current = controller;
  };

  const confirmDeleteSelectedMembers = async () => {
    if (!selectedCampaignMemberIds.length || deletingSelectedMembers) return;
    try {
      setDeletingSelectedMembers(true);
      setDeleteRunCompleted(false);
      setDeleteFailureReasons([]);
      setDeleteProgress({
        total: selectedCampaignMemberIds.length,
        processed: 0,
        deletedCount: 0,
        failedCount: 0,
        batchNo: 0,
        totalBatches: Math.max(1, Math.ceil(selectedCampaignMemberIds.length / 200)),
      });
      const response = await startDeleteCampaignMembersJob(selectedCampaignMemberIds);
      const jobId = extractAsyncJobId(response);
      if (!jobId) {
        throw new Error(
          response?.error ||
            "Delete job did not return a jobId"
        );
      }
      const api = new ApiService({ employee });
      startDeleteMembersStream(api, jobId);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete selected campaign members");
      closeDeleteStreamResources();
      setDeletingSelectedMembers(false);
    }
  };

  const prepContactsForCsvExport = () => {
    try {
      return contacts.map((r) => {
        const contact = JSON.parse(JSON.stringify(r));
        // contact.optionRequests.forEach((or) => {
        //   const { optionId, status } = or;
        //   contact[optionId] = status;
        // });
        // selectedCampaign.options.forEach((option) => {
        //   const { optionId } = option;
        //   if (!contact[optionId]) {
        //     contact[optionId] = "not requested";
        //   }
        // });
        // delete contact.attendanceStatusUpdates;
        // delete contact.comments;
        // delete contact.confirmEmails;
        // delete contact.optionRequests;
        // delete contact.__v;
        // delete contact._id;
        return contact;
      });
    } catch (error) {
      console.log(`prepContactsForCsvExport() error`, error);
    }
  };

  const csvAdjustedContacts = prepContactsForCsvExport();

  return (
    <>
      {contacts && contacts.length ? (
        <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
          <Card className="mb-3 h-fc bot-table">
            <div className="event-image-img">
              <img src={selectedCampaign.imageUrl} className="img-fluid" />
            </div>
            <div className="row">
              <div className="outputHeader">
                Campaign Members
              </div>
              <div className="events-owners-subheading">
                Browse and modify contacts for {selectedCampaign.Name}
                {/* Recommended workflow:
                <ul>
                <li>• Filter the data based on event (or company, etc.)</li>
                <li>
                • Click 'Export Current' to export the filtered data to a
                spreadsheet
                </li>
                <li>• Once handled, click 'Mark Current as Handled'</li>
              </ul> */}
              </div>
              {loadingContactsRefresh ? (
                <div
                  className="campaign-contacts-refresh-banner"
                  role="status"
                  aria-live="polite"
                >
                  <img
                    src={loadingCircleImg}
                    width={18}
                    height={18}
                    alt="Refreshing campaign contacts"
                  />
                  <span>Refreshing campaign contacts...</span>
                </div>
              ) : null}
              <>
                <></>
                <div className="outputBody">
                  <div className="table-wrapper">
                    {/* {showPopupOR && (
                      <ContactStatusUpdatePopup
                        showPopup={showPopupOR}
                        setShowPopup={setShowPopupOR}
                        contact={selectedContactEvent}
                        optionRequest={selectedOptionRequest}
                        position={popupPositionOR}
                        handleUpdateContact={handleUpdateContactOptionsRequest}
                      />
                    )}
                    {showPopupAttendance && (
                      <ContactStatusUpdatePopup
                        attendanceMode={true}
                        showPopup={showPopupAttendance}
                        setShowPopup={setShowPopupAttendance}
                        contact={selectedContactEvent}
                        position={popupPositionAttendance}
                        handleUpdateContact={handleUpdateContactAttendance}
                      />
                    )} */}
                    {
                      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                        <div
                          className={`table-wrapper ${
                            filtersEnabled ? "" : "filters-disabled"
                          }`}
                        >
                          <div className="filters-enable-row">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="filterBypassSwitch"
                                checked={filtersEnabled}
                                onChange={(e) => handleSetFiltersEnabled(e)}
                              ></input>
                              <label
                                className="form-check-label enable-filters-label"
                                htmlFor="filterBypassSwitch"
                              >
                                Enable Filters
                              </label>
                            </div>
                          </div>
                          <div className="filters-row">
                            <FilterInput
                              filterKey="name"
                              placeholder="Name"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              filterKey="email"
                              placeholder="Email"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              filterKey="title"
                              placeholder="Job Title"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              filterKey="accountName"
                              placeholder="Account"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              placeholder="City"
                              filterKey="city"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              placeholder="State"
                              filterKey="state"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <FilterInput
                              placeholder="Country"
                              filterKey="country"
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                          </div>
                          <div className="filters-row">
                            <FilterOption
                              title="Status"
                              filterKey="status"
                              options={campaignStatusOptions}
                              filters={filters}
                              handleSetFilter={handleSetFilter}
                            />
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Comment: </div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={filters?.comments ?? "All"}
                                  onChange={(e) =>
                                    handleSetFilter("comments", e.target.value)
                                  }
                                >
                                  <option value="All">All</option>
                                  <option value={COMMENTS_FILTER_NO_COMMENT}>
                                    (No comment)
                                  </option>
                                  {commentFilterOptions.map((commentValue) => (
                                    <option key={commentValue} value={commentValue}>
                                      {commentValue}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {/* <FilterCheckbox
                              title="Approved and Attending (includes Tentative):"
                              checked={approvedAttendingFilter}
                              handleChecked={handleSetApprovedAttendingFilter}
                            /> */}
                            {/* <FilterCheckbox
                              title="Uncontacted:"
                              checked={uncontactedFilter}
                              handleChecked={handleSetUncontactedFilter}
                            />
                            <FilterCheckbox
                              title="Contacted:"
                              checked={contactedFilter}
                              handleChecked={handleSetContactedFilter}
                            /> */}
                          </div>
                        </div>
                      </motion.div>
                    }
                    {
                      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                        <div className="table-wrapper">
                          <div
                            className="events-owners-subheading"
                            style={{ marginBottom: "8px" }}
                          >
                            {`Totals${filtersEnabled ? ` (filtered)` : ``}`}
                          </div>
                          <div className="totals-row rmono">
                            <div className="total-col">
                              <div className="total-line">
                                <div className="field">Members: </div>
                                <div className="value">
                                  {filteredContacts.length}
                                </div>
                              </div>
                              <div className="total-line option col-black fw-500">
                                <div className="field">Selected: </div>
                                <div className="value">
                                  {selectedContactEmails.length}
                                </div>
                              </div>
                            </div>
                            {/* {selectedCampaign.options.map((option) => {
                              const { shortTitle, optionId } = option;
                              const reorderedStatusEnum = reorderStatusEnum([
                                "APPROVED",
                                "WAITLISTED",
                                "DENIED",
                                "PENDING",
                              ]);
                              return (
                                <div className="total-col">
                                  <div className="total-line option">
                                    <div className="field">{shortTitle}: </div>
                                    <div className="value">
                                      {
                                        filteredContacts.filter((r) =>
                                          r.optionRequests.some(
                                            (or) => or.optionId === optionId
                                          )
                                        ).length
                                      }
                                    </div>
                                  </div>
                                  {Object.entries(reorderedStatusEnum).map(
                                    ([key, status]) => {
                                      return (
                                        <div
                                          className={`total-line col-${status}`}
                                        >
                                          <div className="field">
                                            {toTitleCase(status)}{" "}
                                          </div>
                                          <div className={`value`}>
                                            {
                                              filteredContacts.filter((r) =>
                                                r.optionRequests.some(
                                                  (or) =>
                                                    or.optionId === optionId &&
                                                    or.status === status
                                                )
                                              ).length
                                            }
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              );
                            })} */}
                            <div className="total-col status-totals">
                              {campaignStatusOptions.map((statusLabel) => {
                                const colorClass = getStatusTotalColorClass(statusLabel);
                                return (
                                  <div
                                    key={statusLabel}
                                    className={`total-line fw-500 font-size-11${
                                      colorClass ? ` ${colorClass}` : " option"
                                    }`}
                                  >
                                    <div className="field">{statusLabel}: </div>
                                    <div className="value">
                                      {
                                        filteredContacts.filter(
                                          (c) => c.status === statusLabel
                                        ).length
                                      }
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    }
                    {
                      <div className="text-center mb-3">
                        <div className="mt-3">
                          <button
                            className={`create-event-btn btn btn-sm btn-primary email-options-btn mr-10`}
                            onClick={selectAllBtn}
                          >
                            {`Select All`}
                            <i className="event-owner-add fa-sharp fa-solid fa-user ml-05"></i>
                          </button>
                          <button
                            className={`create-event-btn btn btn-sm btn-primary email-options-btn bulk-email-btn mr-10 ${
                              selectedContactEmails.length ? `` : ` disabled`
                            }`}
                            onClick={unselectAllBtn}
                          >
                            {`Unselect All`}
                          </button>
                          {selectedContactEmails.length > 0 && (
                            <button
                              className="create-event-btn btn btn-sm btn-danger email-options-btn bulk-email-btn mr-10 campaign-delete-selected-btn"
                              onClick={openDeleteSelectedMembersModal}
                            >
                              {`Delete Selected (${selectedContactEmails.length})`}
                              <i className="event-owner-add fa-solid fa-trash ml-05"></i>
                            </button>
                          )}
                          {/* <button
                            className={`create-event-btn btn btn-sm btn-primary email-options-btn bulk-email-btn ${
                              selectedContactEmails.length ? `` : ` disabled`
                            }`}
                            onClick={sendEmailToSelectedContactsBtn}
                          >
                            {`Send Email${
                              selectedContactEmails.length === 1 ? "" : "s"
                            } To Selected (${
                              selectedContactEmails.length
                            } contacts)`}
                            <i className="event-owner-add fa-sharp fa-solid fa-envelope ml-05"></i>
                          </button> */}
                          {/* <ExportJsonCsv
                            headers={headers}
                            items={csvAdjustedContacts}
                            className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                          >
                            {`Export All Contacts`}
                            <i className="event-owner-add fa-sharp fa-solid fa-download ml-05"></i>
                          </ExportJsonCsv> */}
                        </div>
                      </div>
                    }
                    {selectedContactEmails.length > 0 && (
                      <div className="campaign-bulk-update-row">
                        <div className="campaign-bulk-update-section">
                          <div className="campaign-bulk-update-title">
                            {`Bulk update status (${selectedContactEmails.length} selected)`}
                          </div>
                          <div className="campaign-bulk-update-controls">
                            <select
                              className="form-control rmono"
                              value={bulkStatus}
                              onChange={(e) => setBulkStatus(e.target.value)}
                            >
                              <option value="">Select status...</option>
                              {campaignStatusOptions.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={!bulkStatus || bulkUpdatingStatus}
                              onClick={handleBulkUpdateStatus}
                            >
                              {bulkUpdatingStatus ? "Updating..." : "Apply"}
                            </button>
                          </div>
                          {bulkStatusIsSentType ? (
                            <label className="campaign-bulk-lastsent-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={bulkUpdateLastSent}
                                onChange={(e) =>
                                  setBulkUpdateLastSent(e.target.checked)
                                }
                              />
                              <span>
                                Also update Last Sent &amp; Outreach Counter
                              </span>
                            </label>
                          ) : null}
                        </div>
                        <div className="campaign-bulk-update-section">
                          <div className="campaign-bulk-update-title">
                            {`Bulk update comment (${selectedContactEmails.length} selected)`}
                          </div>
                          <div className="campaign-bulk-update-controls">
                            <input
                              type="text"
                              className="form-control rmono"
                              value={bulkComment}
                              onChange={(e) => setBulkComment(e.target.value)}
                              placeholder="Comment for selected members"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={bulkUpdatingComment}
                              onClick={handleBulkUpdateComment}
                            >
                              {bulkUpdatingComment ? "Updating..." : "Apply"}
                            </button>
                          </div>
                        </div>
                        <div className="campaign-bulk-update-section">
                          <div className="campaign-bulk-update-title">
                            {`Bulk update email version (${selectedContactEmails.length} selected)`}
                          </div>
                          <div className="campaign-bulk-update-controls">
                            <input
                              type="text"
                              className="form-control rmono"
                              value={bulkEmailVersion}
                              onChange={(e) => setBulkEmailVersion(e.target.value)}
                              placeholder="Email version to set on selected campaign members"
                              maxLength={250}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={bulkUpdatingEmailVersion}
                              onClick={handleBulkUpdateEmailVersion}
                            >
                              {bulkUpdatingEmailVersion ? "Updating..." : "Apply"}
                            </button>
                          </div>
                        </div>
                        <div className="campaign-bulk-update-section">
                          <div className="campaign-bulk-update-title">
                            {`Bulk update campaign sent as (${selectedContactEmails.length} selected)`}
                          </div>
                          <div className="campaign-bulk-update-controls">
                            <div className="campaign-bulk-select-wrap">
                              <Select
                                className="rmono campaign-sent-as-select"
                                classNamePrefix="campaign-sent-as-select"
                                styles={salesforceUserSelectStyles}
                                value={bulkCampaignSentAs}
                                onChange={setBulkCampaignSentAs}
                                options={salesforceUserOptions}
                                isSearchable
                                isClearable
                                placeholder="Select Salesforce user"
                                noOptionsMessage={() => "No Salesforce users found"}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={
                                !bulkCampaignSentAs?.value || bulkUpdatingCampaignSentAs
                              }
                              onClick={handleBulkUpdateCampaignSentAs}
                            >
                              {bulkUpdatingCampaignSentAs ? "Updating..." : "Apply"}
                            </button>
                          </div>
                        </div>
                        <div className="campaign-bulk-update-section">
                          <div className="campaign-bulk-update-title">
                            {`Bulk update last sent (${selectedContactEmails.length} selected)`}
                          </div>
                          <div className="campaign-bulk-update-controls">
                            <DatePicker
                              selected={etDatetimeLocalToPickerDate(bulkLastSent)}
                              onChange={(date) =>
                                setBulkLastSent(pickerDateToEtDatetimeLocal(date))
                              }
                              showTimeSelect
                              timeIntervals={1}
                              timeCaption="Time"
                              dateFormat={LAST_SENT_DATE_FORMAT}
                              className="form-control rmono campaign-bulk-datetime-input"
                              wrapperClassName="campaign-bulk-datepicker-wrapper"
                              placeholderText="Select last sent (ET)"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={!bulkLastSent || bulkUpdatingLastSentField}
                              onClick={handleBulkUpdateLastSentField}
                            >
                              {bulkUpdatingLastSentField ? "Updating..." : "Apply"}
                            </button>
                          </div>
                          <div className="campaign-modifier-sent-note mt-2">
                            <i className="fa fa-circle-info mr-05" />
                            The Outreach Counter will also be updated for affected
                            contacts.
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="progress-bar-container">
                      {emailJobs &&
                      emailJobs.some(
                        (ej) =>
                          ej.campaignId === selectedCampaign.campaignId &&
                          !ej.completed
                      ) ? (
                        <>
                          {emailJobs
                            .filter(
                              (ej) =>
                                ej.campaignId === selectedCampaign.campaignId &&
                                !ej.completed
                            )
                            .map((ej, key) => {
                              const {
                                totalEmails,
                                totalSent,
                                totalSuccessful,
                                totalFailed,
                                sentContactEmails,
                              } = ej;
                              return (
                                <div className="w-100" key={key}>
                                  <ProgressBar>
                                    <ProgressBar
                                      animated
                                      striped
                                      variant="success"
                                      max={totalEmails}
                                      now={totalSuccessful}
                                      key={1}
                                    />
                                    <ProgressBar
                                      animated
                                      striped
                                      variant="danger"
                                      max={totalEmails}
                                      now={totalFailed}
                                      key={2}
                                    />
                                  </ProgressBar>
                                </div>
                              );
                            })}
                        </>
                      ) : null}
                    </div>
                    <Table
                      responsive
                      className="campaign-dashboard-table header-border "
                    >
                      <thead>
                        <tr>
                          <th className="width50 ">
                            <div className="form-check custom-checkbox checkbox-success check-lg me-3 bs_exam_topper_all">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="checkAll"
                                required
                                defaultChecked={false}
                                checked={
                                  !pageContacts.some(
                                    (pr) =>
                                      !selectedContactEmails.includes(pr.email)
                                  )
                                }
                                onChange={toggleSelectAllPageContacts}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="checkAll"
                              ></label>
                            </div>
                          </th>
                          <th>Contact</th>
                          <th>Account</th>
                          <th>Title</th>
                          <th>Location</th>
                          <th>Comments</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageContacts.map((contact, i) => {
                          const {
                            accountId,
                            accountName,
                            campaignId,
                            contactId,
                            email,
                            id,
                            name,
                            status: contactStatus,
                            comments,
                            title,
                            linkedIn,
                            city,
                            state,
                            country
                          } = contact;
                          if(contactId === "003EX000003UxdNYAS"){

                            console.log(`contact`, contact);
                          }
                          const pfpImg = "https://i.ibb.co/vvmpVNSS/default-pfp.jpg"; // default linkedin pfp

                          return (
                            <tr key={`tr_row_${i}`}>
                              <td>
                                <div className="form-check custom-checkbox checkbox-success check-lg me-3 bs_exam_topper">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="customCheckBox2"
                                    required
                                    defaultChecked={false}
                                    checked={selectedContactEmails.includes(
                                      contact.email
                                    )}
                                    onChange={(event) =>
                                      toggleSelectContact(event, contact)
                                    }
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="user-container-outer">
                                  <img
                                    className="pfp-img"
                                    src={pfpImg}
                                    width={46}
                                  />
                                  <div className="user-container">
                                    <div className="name">
                                      <a className="blue-link d-inline-block" target="_blank" href={shiftHeld && id ? `https://landmarkventures.lightning.force.com/lightning/r/CampaignMember/${id}/view` : `https://landmarkventures.lightning.force.com/lightning/r/Contact/${contactId}/view`} title={shiftHeld && id ? "Open Campaign Member in Salesforce" : "Open Contact in Salesforce"}>{name}</a>
                                     {linkedIn && (<div className="email d-inline-block" style={{marginLeft:"12px"}}>
                                      <a className="blue-link" target="_blank" href={linkedIn}><img src="https://upload.wikimedia.org/wikipedia/commons/8/81/LinkedIn_icon.svg" width={14}/>{" "}</a>
                                      <span
                                        className="clipboard-icon hvr-shrink"
                                        onClick={() => copyText(linkedIn, "LinkedIn URL")}
                                      >
                                        <FileCopyOutlinedIcon
                                          style={{
                                            width: "0.7em",
                                            height: "0.7em",
                                          }}
                                        />
                                      </span>
                                    </div>)}
                                    </div>
                                    <div className="email">
                                      {email}{" "}
                                      <span
                                        className="clipboard-icon hvr-shrink"
                                        onClick={() => copyText(email, "email")}
                                      >
                                        <FileCopyOutlinedIcon
                                          style={{
                                            width: "0.7em",
                                            height: "0.7em",
                                          }}
                                        />
                                      </span>
                                    </div>
                                   
                                      
                                    <div className="job">
                                      {title} at <a className="blue-link" target="_blank" href={`https://landmarkventures.lightning.force.com/lightning/r/Account/${accountId}/view`}>{accountName}</a>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td><a className="blue-link" target="_blank" href={`https://landmarkventures.lightning.force.com/lightning/r/Account/${accountId}/view`}>{accountName}</a></td>
                              <td>{title}</td>
                              <td>
                                {([city, state, country].some(Boolean)) ? (
                                  <>
                                    {[city, state].filter(Boolean).join(", ")}
                                    {(city || state) && <br />}
                                    {country || null}
                                  </>
                                ) : "N/A"}
                              </td>
                              <td className="campaign-member-comments-cell" title={comments || ""}>
                                {editingCommentId === id ? (
                                  <div className="comment-edit-wrap">
                                    <input
                                      type="text"
                                      className="form-control rmono font-size-12 comment-edit-input"
                                      value={commentDraft}
                                      onChange={(e) => setCommentDraft(e.target.value)}
                                      placeholder="Add a comment"
                                      autoFocus
                                    />
                                    {commentDraft !== String(comments || "") ? (
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-primary comment-save-btn"
                                        disabled={savingCommentId === id}
                                        onClick={() =>
                                          handleSaveComment(id, commentDraft, comments)
                                        }
                                      >
                                        {savingCommentId === id ? "Saving..." : "Save"}
                                      </button>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="comment-display">
                                    <span className="comment-text">
                                      {String(comments || "").trim() || "-"}
                                    </span>
                                    <button
                                      type="button"
                                      className="comment-edit-link"
                                      onClick={() => beginEditComment(id, comments)}
                                    >
                                      edit
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td>      <select
        className="form-control rmono font-size-12"
        value={contactStatus || ""}
        onChange={(e) => handleStatusChange(e, id)}
      >
        {(contactStatus && !campaignStatusOptions.includes(contactStatus)
          ? [contactStatus, ...campaignStatusOptions]
          : campaignStatusOptions
        ).map(s => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <div className="d-flex justify-content-between">
                      <span className="table-index">
                        Page{" "}
                        <strong>
                          {pageIndex + 1} of {pageCount}
                        </strong>
                        {""}
                      </span>
                      <span className="table-index">
                        Rows per page :{" "}
                        <input
                          type="number"
                          className="ml-2"
                          defaultValue={rowsPerPage}
                          min={1}
                          max={500}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                          }}
                        />
                      </span>
                      <span className="table-index">
                        Go to page :{" "}
                        <input
                          type="number"
                          className="ml-2"
                          defaultValue={pageIndex + 1}
                          min={1}
                          max={pageCount}
                          onChange={(e) => {
                            const pageNumber = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            goToPage(pageNumber);
                          }}
                        />
                      </span>
                    </div>
                    <div className="text-center mb-3">
                      <div className="filter-pagination  mt-3">
                        <button
                          className=" previous-button"
                          onClick={() => goToPage(0)}
                          disabled={!canPreviousPage}
                        >
                          {"<<"}
                        </button>

                        <button
                          className="previous-button"
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        >
                          Previous
                        </button>
                        <button
                          className="next-button"
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        >
                          Next
                        </button>
                        <button
                          className=" next-button"
                          onClick={() => goToPage(pageCount - 1)}
                          disabled={!canNextPage}
                        >
                          {">>"}
                        </button>
                      </div>
                    </div>
                    <div className="text-center mb-3">
                      <div className="mt-3">
                        {/* <button
                          className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                          onClick={handleExportData}
                        >
                          {`Export All RSVPs`}
                          <i className="event-owner-add fa-sharp fa-solid fa-download ml-05"></i>
                        </button> */}
                        <ExportJsonCsv
                          headers={headers}
                          items={csvAdjustedContacts}
                          className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                        >
                          {`Export All Contacts`}
                          <i className="event-owner-add fa-sharp fa-solid fa-download ml-05"></i>
                        </ExportJsonCsv>
                      </div>
                    </div>
                  </div>
                </div>
              </>
              {!contacts ? (
                <div className="text-center">
                  <img
                    className="m-auto mt-3 mb-3"
                    width={74}
                    src={loadingCircleImg}
                  />
                </div>
              ) : null}
            </div>
          </Card>
        </motion.div>
      ) : loadingContactsRefresh ? (
        <div className="no-contacts-msg">
          <img
            className="m-auto mt-2 mb-2"
            width={34}
            src={loadingCircleImg}
            alt="Refreshing contacts"
          />
          <div>Refreshing campaign contacts...</div>
        </div>
      ) : (
        <div className="no-contacts-msg">No contacts found for this campaign</div>
      )}
      <Modal
        show={showDeleteMembersModal}
        onHide={closeDeleteSelectedMembersModal}
        centered
      >
        <Modal.Header closeButton={!deletingSelectedMembers}>
          <Modal.Title>Delete Campaign Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!deletingSelectedMembers ? (
            <div>
              {`Are you sure you want to delete ${
                selectedCampaignMemberIds.length
              } selected campaign member${
                selectedCampaignMemberIds.length === 1 ? "" : "s"
              } from Salesforce? This action cannot be undone.`}
            </div>
          ) : (
            <div>
              <div className="mb-2">
                {`Deleting ${deleteProgress.total} campaign members in batches${
                  deleteProgress.totalBatches > 0
                    ? ` (${deleteProgress.batchNo}/${deleteProgress.totalBatches})`
                    : ""
                }...`}
              </div>
              <ProgressBar
                className="campaign-delete-progress"
                now={
                  deleteProgress.total > 0
                    ? Math.round((deleteProgress.processed / deleteProgress.total) * 100)
                    : 0
                }
                label={`${
                  deleteProgress.total > 0
                    ? Math.round((deleteProgress.processed / deleteProgress.total) * 100)
                    : 0
                }%`}
                animated
                striped
              />
              <div className="mt-2 font-size-12">
                {`Processed: ${deleteProgress.processed}/${deleteProgress.total} | Deleted: ${deleteProgress.deletedCount} | Failed: ${deleteProgress.failedCount}`}
              </div>
            </div>
          )}
          {!deletingSelectedMembers &&
            deleteRunCompleted &&
            deleteFailureReasons.length > 0 && (
              <div className="mt-3">
                <div className="font-size-12 fw-500 mb-1">
                  Failure reasons
                </div>
                <div className="font-size-12">
                  {deleteFailureReasons.map((entry, idx) => (
                    <div key={`delete-failure-reason-${idx}`}>
                      {`${entry.count}x - ${entry.reason}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={closeDeleteSelectedMembersModal}
            disabled={deletingSelectedMembers}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={confirmDeleteSelectedMembers}
            disabled={
              deletingSelectedMembers ||
              !selectedCampaignMemberIds.length ||
              deleteRunCompleted
            }
          >
            {deletingSelectedMembers ? "Deleting..." : "Start Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const PasswordPrompt = ({ employee, setPassword, submitPassword }) => {
  const handleInputChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitPassword(employee);
  };

  return (
    <div className="d-flex">
      <input
        onChange={handleInputChange}
        type="employee"
        className="form-control w-100 mt-1 inline-input"
        placeholder="Password"
        value={employee}
      ></input>
      <div
        className="btn btn-primary light btn-xs mt-1 me-1 w-fc inline-btn"
        onClick={(e) => handleSubmit(e)}
      >
        Submit
      </div>
    </div>
  );
};

const SelectCampaign = ({
  selectedCampaign,
  selectedCampaignId,
  setSelectedCampaignId,
  campaigns,
  salesforceUsers = [],
  loading,
  loadingSelectedCampaign,
  employee,
  refreshSelectedCampaign,
}) => {
  const campaignOptions = [...(campaigns || [])]
    .reverse()
    .map((campaign) => {
      const id = String(campaign?.Id || "").trim();
      const name = String(campaign?.Name || "").trim();
      return {
        value: id,
        label: id ? `${name} (${id})` : name,
      };
    })
    .filter((option) => option.value && option.label);

  const selectedCampaignOption =
    campaignOptions.find((option) => option.value === String(selectedCampaignId || "")) || null;

  const handleCampaignChange = (option) => {
    const nextId = String(option?.value || "").trim();
    setSelectedCampaignId(nextId || null);
  };
  const campaignSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 30,
      height: 30,
      borderColor: state.isFocused ? "#8caee0" : "#ced4da",
      boxShadow: "none",
      borderRadius: 4,
      fontSize: 12,
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    }),
    valueContainer: (base) => ({
      ...base,
      height: 30,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: 12,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
      fontSize: 12,
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: 12,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 30,
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: 6,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: 6,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 20,
      fontSize: 12,
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 12,
      backgroundColor: state.isFocused ? "#f0f4fa" : base.backgroundColor,
      color: "#212529",
    }),
  };

  return (
    <>
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table">
          <div className="row">
            <div className="outputHeader">Campaign Uploader</div>
            <div className="events-owners-subheading">
              Upload a contact list to a Salesforce campaign
            </div>
            <>
              <div className="events-owners-subheading">
                {!loading ? `Select a Salesforce Campaign` : `Loading Salesforce Campaigns`}
              </div>
              <div
                className="event-owners-wrapper"
                style={{ marginBottom: "12px" }}
              >
                {loading ? (
                <div className="text-center">
                  <img
                    className="m-auto mt-3 mb-3"
                    width={74}
                    src={loadingCircleImg}
                  />
                </div>
              ) : <div className="filters-row">
  <div className="filter-option">
    <div className="input-group">
      <span className="input-group-text">
        <div className="option-title">Campaign: </div>{" "}
      </span>
      <div className="p-0 border-0" style={{ width: 400 }}>
        <Select
          className="rmono"
          styles={campaignSelectStyles}
          value={selectedCampaignOption}
          onChange={handleCampaignChange}
          options={campaignOptions}
          isSearchable
          isClearable
          placeholder="Select a campaign"
          noOptionsMessage={() => "No campaigns found"}
        />
      </div>
    </div>
  </div>
</div>}
              </div>
{loadingSelectedCampaign ? (
              <div className="text-center mt-2 mb-2">
                <img
                  className="m-auto mt-2 mb-2"
                  width={40}
                  src={loadingCircleImg}
                  alt="Loading selected campaign"
                />
              </div>
            ) : selectedCampaign && (
              <CampaignContactUploader
                campaignId={selectedCampaignId}
                selectedCampaign={selectedCampaign}
                employee={employee}
                salesforceUsers={salesforceUsers}
                campaigns={campaigns}
                refreshSelectedCampaign={refreshSelectedCampaign}
              />
            )}
            </>
          </div>
        </Card>
      </motion.div>
    </>
  );
};

const CampaignUploadMetricsPanel = ({ employee, selectedCampaign }) => {
  const [showMetrics, setShowMetrics] = useState(false);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const campaignId = String(
    selectedCampaign?.Id || selectedCampaign?.campaignId || ""
  ).trim();

  const loadMetrics = async (
    targetCampaignId = campaignId,
    includeAllCampaigns = showAllCampaigns
  ) => {
    const normalizedCampaignId = String(targetCampaignId || "").trim();
    if (!employee) {
      setRows([]);
      return;
    }
    if (!includeAllCampaigns && !normalizedCampaignId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const api = new ApiService({ employee });
      const endpoint = includeAllCampaigns
        ? "landmark/salesforce/uploadMetrics"
        : `landmark/salesforce/uploadMetrics/${encodeURIComponent(
            normalizedCampaignId
          )}`;
      const result = await api.get(endpoint, { limit: 200 });
      setRows(Array.isArray(result?.rows) ? result.rows : []);
    } catch (loadError) {
      console.error(loadError);
      setRows([]);
      setError("Failed to load upload metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showMetrics) return;
    loadMetrics(campaignId, showAllCampaigns);
  }, [showMetrics, showAllCampaigns, campaignId, employee?.employeeId]);

  return (
    <div className="text-center mt-2 mb-4">
      <button
        type="button"
        className="btn btn-link p-0 text-muted font-size-12"
        onClick={() => setShowMetrics((prev) => !prev)}
      >
        {showMetrics ? "Hide upload metrics" : "View upload metrics"}
      </button>

      {showMetrics ? (
        <div className="table-wrapper mt-2 text-start">
          <div className="events-owners-subheading mb-1 d-flex align-items-center justify-content-between">
            <span>
              Campaign uploader metrics
              {showAllCampaigns
                ? " (all campaigns)"
                : campaignId
                ? " (selected campaign)"
                : ""}
            </span>
            <button
              type="button"
              className="btn btn-link p-0 text-muted font-size-11"
              onClick={() => setShowAllCampaigns((prev) => !prev)}
            >
              {showAllCampaigns
                ? "View selected campaign only"
                : "View all campaigns"}
            </button>
          </div>
          {loading ? (
            <div className="font-size-12 text-muted">Loading metrics...</div>
          ) : error ? (
            <div className="font-size-12 text-danger">{error}</div>
          ) : !showAllCampaigns && !campaignId ? (
            <div className="font-size-12 text-muted">
              Select a campaign, or click "View all campaigns".
            </div>
          ) : rows.length === 0 ? (
            <div className="font-size-12 text-muted">
              {showAllCampaigns
                ? "No upload metrics recorded yet."
                : "No upload metrics recorded for this campaign yet."}
            </div>
          ) : (
            <Table responsive className="campaign-dashboard-table header-border">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Finished</th>
                  <th>Campaign</th>
                  <th>Uploader</th>
                  <th>File</th>
                  <th>CSV Rows</th>
                  <th>Filtered</th>
                  <th>Contacts Added</th>
                  <th>Success</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const uploaderLabel =
                    String(row?.employeeName || "").trim() ||
                    String(row?.employeeEmail || "").trim() ||
                    String(row?.employeeId || "").trim() ||
                    "-";
                  const startedAt = row?.startedAt
                    ? new Date(row.startedAt).toLocaleString()
                    : "-";
                  const finishedAt = row?.finishedAt
                    ? new Date(row.finishedAt).toLocaleString()
                    : row?.uploadState === "started"
                    ? "In progress"
                    : "-";
                  return (
                    <tr key={String(row?._id || row?.jobId || index)}>
                      <td>{startedAt}</td>
                      <td>{finishedAt}</td>
                      <td>{String(row?.campaignName || row?.campaignId || "-")}</td>
                      <td>{uploaderLabel}</td>
                      <td>{String(row?.uploadedFileName || "-")}</td>
                      <td>{addCommas(Number(row?.uploadedCsvContactsCount || 0))}</td>
                      <td>{`${addCommas(
                        Number(row?.filteredOutTotal || 0)
                      )} (verifier ${addCommas(
                        Number(row?.filteredOutByVerifier || 0)
                      )}, other ${addCommas(
                        Number(row?.filteredOutOther || 0)
                      )})`}</td>
                      <td>{addCommas(Number(row?.linkedCampaignMembers || 0))}</td>
                      <td>{row?.completedSuccessfully ? "Yes" : "No"}</td>
                      <td>{row?.hasErrors ? row?.errorMessage || "Yes" : "No"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      ) : null}
    </div>
  );
};

const ContactUploadForm = ({ employee, campaignId, selectedCampaign, refreshSelectedCampaign }) => {
  const [originalData, setOriginalData] = useState([]);
  const [csvDiagnostics, setCsvDiagnostics] = useState({
    parsedRows: 0,
    nonEmptyRows: 0,
    parseWarnings: 0,
  });
  const [detectedListSource, setDetectedListSource] = useState("");
  const [emailKey, setEmailKey] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [masterCsvData, setMasterCsvData] = useState([]);
  const [masterCsvFileName, setMasterCsvFileName] = useState("");
  const [status, setStatus] = useState(getRecommendedStatusForCampaignType(selectedCampaign?.RecordType?.Name));
  const [statusManuallySet, setStatusManuallySet] = useState(false);
  const [skipExistingCampaignMembers, setSkipExistingCampaignMembers] = useState(true);
  const [runContactsThroughMillionVerifier, setRunContactsThroughMillionVerifier] = useState(true);
  const [campaignMemberComment, setCampaignMemberComment] = useState("");
  const [masterLoading, setMasterLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [finalData, setFinalData] = useState([]);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [uploadingToSf, setUploadingToSf] = useState(false);

  // NEW: SSE log viewer state/refs
  const [logLines, setLogLines] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingUploadHistory, setLoadingUploadHistory] = useState(false);
  const esControllerRef = useRef(null);
  const logRef = useRef(null);
  const streamRetryTimeoutRef = useRef(null);

  const csvInputRef = useRef(null);
  const masterCsvInputRef = useRef(null);

  const prevCampaignIdRef = useRef(selectedCampaign?.Id ?? null);

  const handleUploadStatusChange = (e) => {
    const st = e.target.value;
    if(st) {
      setStatusManuallySet(true);
    }
    setStatus(st);
  };

// react to campaign changes
useEffect(() => {
  const newId = selectedCampaign?.Id ?? null;
  const prevId = prevCampaignIdRef.current;
  const changed = prevId !== newId;

  // derive what the manual flag will effectively be *this* run
  const nextManual = changed ? false : statusManuallySet;

  // update ref for next runs
  if (changed) {
    prevCampaignIdRef.current = newId;
    setStatusManuallySet(false); // async, but we already used nextManual above
  }

  // only (re)set recommended status when not manually set
  if (!nextManual) {
    const rec = getRecommendedStatusForCampaignType(
      selectedCampaign?.RecordType?.Name
    );
    setStatus(rec);
  }
}, [selectedCampaign?.Id, selectedCampaign?.RecordType?.Name, statusManuallySet]);

  // helpers
  const safeParse = (s) => {
    try { return JSON.parse(s); } catch { return s; }
  };
  const formatLogLine = (payload) => {
    if (!payload) return "";
    const ts = new Date(payload.ts || Date.now()).toLocaleTimeString();
    if (typeof payload === "string") return `[${ts}] ${payload}`;
    if (payload.msg) return `[${ts}] ${payload.msg}`;
    return `[${ts}] ${typeof payload === "object" ? JSON.stringify(payload) : String(payload)}`;
  };
  const appendLog = (line) => setLogLines((prev) => [...prev, line]);
  const extractAsyncJobId = (response) => {
    if (!response) return null;

    let payload = response;
    if (typeof response === "string") {
      try {
        payload = JSON.parse(response);
      } catch {
        return null;
      }
    }

    const candidates = [
      payload?.jobId,
      payload?.jobID,
      payload?.taskId,
      payload?.taskID,
      payload?.id,
      payload?.data?.jobId,
      payload?.data?.jobID,
      payload?.data?.taskId,
      payload?.data?.taskID,
      payload?.data?.id,
    ];

    return candidates.find((value) => typeof value === "string" && value.trim()) || null;
  };
  const loadUploadHistory = async (targetCampaignId = campaignId) => {
    const cid = String(targetCampaignId || "").trim();
    if (!cid || !employee) {
      setUploadHistory([]);
      return;
    }
    setLoadingUploadHistory(true);
    try {
      const api = new ApiService({ employee });
      const result = await api.get(
        `landmark/salesforce/uploadHistory/${encodeURIComponent(cid)}`,
        { limit: 15 }
      );
      const history = Array.isArray(result?.history) ? result.history : [];
      setUploadHistory(history);
    } catch (err) {
      console.error(err);
      setUploadHistory([]);
    } finally {
      setLoadingUploadHistory(false);
    }
  };

  // Autoscroll when new lines come in
  useEffect(() => {
    if (!autoScroll) return;
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines, autoScroll]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (streamRetryTimeoutRef.current) {
        clearTimeout(streamRetryTimeoutRef.current);
        streamRetryTimeoutRef.current = null;
      }
      if (esControllerRef.current) {
        esControllerRef.current.close?.();
        esControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    loadUploadHistory(campaignId);
  }, [campaignId, employee?.employeeId]);

  const startStream = (api, jobId, retryAttempt = 0) => {
    if (!jobId) return;
    const MAX_UNKNOWN_JOB_RETRIES = 5;

    // Close old stream if any
    if (esControllerRef.current) {
      esControllerRef.current.close?.();
      esControllerRef.current = null;
    }
    if (streamRetryTimeoutRef.current) {
      clearTimeout(streamRetryTimeoutRef.current);
      streamRetryTimeoutRef.current = null;
    }

    setStreaming(true);
    if (retryAttempt === 0) {
      appendLog(`Opened stream for job ${jobId}`);
    } else {
      appendLog(`Retrying stream for job ${jobId} (${retryAttempt}/${MAX_UNKNOWN_JOB_RETRIES})`);
    }

    const controller = api.openJobStream(jobId, {
      onOpen: (_ev, meta) => {
        appendLog(`[${new Date().toLocaleTimeString()}] SSE opened → ${meta?.url ?? ""}`);
      },
      onLog: (payloadOrString) => {
        // server may send objects or plain strings
        const line =
          typeof payloadOrString === "string"
            ? formatLogLine(payloadOrString)
            : formatLogLine(payloadOrString);
        appendLog(line);
      },
      onDone: async (payload) => {
        setUploadSummary(payload?.uploadSummary || null);
        appendLog(formatLogLine({ msg: "Job complete ✅", ts: payload?.ts || Date.now() }));
        setStreaming(false);
        toast.success("Contacts uploaded to Salesforce Campaign");
        esControllerRef.current = null; // already closed by wrapper
        // refresh campaign data without unmounting uploader/log state
        refreshSelectedCampaign({ silent: true, showContactsRefresh: true });
        await loadUploadHistory(campaignId);
      },
      onError: (payload) => {
        const errorMsg = String(payload?.error || "").trim();
        const isUnknownJobIdError =
          errorMsg.toLowerCase() === "unknown jobid" ||
          errorMsg.toLowerCase().includes("unknown jobid");

        if (isUnknownJobIdError && retryAttempt < MAX_UNKNOWN_JOB_RETRIES) {
          const nextAttempt = retryAttempt + 1;
          const retryDelayMs = Math.min(1500 * nextAttempt, 5000);
          appendLog(
            formatLogLine({
              msg: `Stream could not find job yet. Retrying in ${Math.round(
                retryDelayMs / 1000
              )}s...`,
              ts: Date.now(),
            })
          );
          esControllerRef.current?.close?.();
          esControllerRef.current = null;
          streamRetryTimeoutRef.current = setTimeout(() => {
            startStream(api, jobId, nextAttempt);
          }, retryDelayMs);
          return;
        }

        if (payload?.error) appendLog(formatLogLine({ msg: `Error: ${payload.error}` }));
        appendLog(formatLogLine({ msg: "Stream closed (error or server finished).", ts: Date.now() }));
        esControllerRef.current?.close?.();
        setStreaming(false);
        esControllerRef.current = null;
      },
    });

    esControllerRef.current = controller;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);

    Papa.parse(file, {
      complete: (result) => {
        const parsedRows = Array.isArray(result?.data) ? result.data : [];
        const parsedHeaders = Array.isArray(result?.meta?.fields)
          ? result.meta.fields
          : [];
        const fallbackHeaders = Object.keys(parsedRows?.[0] || {});
        const listSource = detectContactListSourceFromHeaders(
          parsedHeaders.length ? parsedHeaders : fallbackHeaders
        );
        const emailsFromCsv = mapEmails(parsedRows, emailKey, setEmailKey);
        const nonEmptyRows = parsedRows.filter((row) =>
          Object.values(row || {}).some(
            (value) => String(value ?? "").trim() !== ""
          )
        ).length;
        setOriginalData(parsedRows);
        setDetectedListSource(listSource);
        setCsvDiagnostics({
          parsedRows: parsedRows.length,
          nonEmptyRows,
          parseWarnings: Array.isArray(result?.errors) ? result.errors.length : 0,
        });
        console.log('Parsed CSV data:', parsedRows);
        setCsvData(emailsFromCsv);
        // reset any previous downstream state
        setFinalData([]);
        setDuplicateCount(0);
      },
      header: true,
    });
  };

  const handleMasterFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMasterCsvFileName(file.name);
    setMasterLoading(true);

    setTimeout(() => {
      Papa.parse(file, {
        complete: (result) => {
          const emailsFromCsv = mapEmails(result.data);
          setMasterCsvData(emailsFromCsv);
          setMasterLoading(false);
        },
        header: true,
      });
    }, 500);
  };

  const handleCsvUploadBtnClick = () => {
    csvInputRef.current?.click();
  };

  const handleMasterCsvUploadBtnClick = () => {
    masterCsvInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    if (csvInputRef.current) csvInputRef.current.value = "";
    setCsvFileName("");
    setCsvData([]);
    setOriginalData([]);
    setDetectedListSource("");
    setCsvDiagnostics({
      parsedRows: 0,
      nonEmptyRows: 0,
      parseWarnings: 0,
    });
    setFinalData([]);
    setDuplicateCount(0);
    setLogLines([]);
    setUploadSummary(null);
    if (esControllerRef.current) {
      esControllerRef.current.close?.();
      esControllerRef.current = null;
    }
    setStreaming(false);
  };

  async function getSalesforceContacts() {
    try {
      const api = new ApiService({ employee });
      const result = await api.get(`landmark/salesforce/getContacts`, null, null, { timeout: 0 });
      setMasterLoading(false);
      if (!result || !result.meta || !result.contactsEmails) {
        toast.error("An error occurred when retrieving Salesforce contacts");
        return;
      }
      setMasterCsvData(result.contactsEmails);
      setMasterCsvFileName(`${csvFileName}-PARSED.csv`);
    } catch (error) {
      toast.error("An error occurred when retrieving Salesforce contacts");
      console.error(error);
    }
  }

  const handleSalesforceBtn = () => {
    setMasterLoading(true);
    setMasterCsvData([]);
    setMasterCsvFileName("");
    setTimeout(() => {
      getSalesforceContacts();
    }, 800);
  };

  async function addContactsFromDashboard() {
    setMasterLoading(true);
    setMasterCsvData([]);
    setMasterCsvFileName("");
    try {
      const api = new ApiService({ employee });
      const result = await api.post(
        `landmark/contacts/addContactsFromDashboard`,
        { contacts: originalData }
      );
      setMasterLoading(false);
      if (!result || !result.contacts || !result.contacts.length) {
        toast.error("An error occurred when adding contacts");
        return;
      }
      toast.success("Contact Parsing Successful", { duration: 5000 });

      const fileName = `${csvFileName}-PARSED.csv`;
      try {
        const parser = new Parser();
        const csv = parser.parse(result.contacts);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("File Downloaded");
        }
      } catch (err) {
        console.error(err);
      }
    } catch (error) {
      toast.error("An error occurred when retrieving Salesforce contacts");
      console.error(error);
    }
  }

  // UPDATED: now expects { jobId } then uses ApiService.openJobStream
  const uploadContactsToSalesforce = async () => {
    if (!campaignId || !status) {
      toast.error("Missing campaignId or status");
      return;
    }
    try {
      setUploadingToSf(true);
      setLogLines([]); // fresh log for this run
      setUploadSummary(null);

      const api = new ApiService({ employee });
      const body = {
        contactsData: originalData, // send full parsed rows
        campaignId,
        status,
        skipExistingCampaignMembers,
        runContactsThroughMillionVerifier,
        campaignMemberComment: String(campaignMemberComment || "").trim(),
        listSource: detectedListSource || "",
      };

      appendLog("Starting upload…");
      let uploadEndpoint = `landmark/salesforce/upsertContactsToCampaignOptimized`;
      let result = await api.post(uploadEndpoint, body, null, { timeout: 0 });
      const maybeError = String(result?.error || "").toLowerCase();
      const endpointMissing =
        maybeError.includes("cannot post") ||
        maybeError.includes("404") ||
        maybeError.includes("not found");
      if (endpointMissing) {
        uploadEndpoint = `landmark/salesforce/upsertContactsToCampaign`;
        appendLog("Optimized upload endpoint unavailable, falling back to original...");
        result = await api.post(uploadEndpoint, body, null, { timeout: 0 });
      }

      const jobId = extractAsyncJobId(result);
      if (!jobId) {
        if (result?.error) {
          appendLog(`Server rejected upload: ${result.error}`);
          toast.error(result.error);
        } else {
          const payloadPreview =
            typeof result === "string" ? result : JSON.stringify(result ?? {});
          appendLog(`Did not receive a jobId from server. Payload: ${payloadPreview}`);
          toast.error("Failed to start job (no jobId).");
        }
        return;
      }

      appendLog(`Upload job started via ${uploadEndpoint}`);
      startStream(api, jobId);
    } catch (err) {
      console.error(err);
      appendLog(`Client error: ${err.message || String(err)}`);
      toast.error("Failed to upload contacts to Salesforce");
    } finally {
      setUploadingToSf(false);
    }
  };

  const downloadFinalData = () => {
    const fileName = `deduped-emails.csv`;
    try {
      const parser = new Parser();
      const csv = parser.parse(finalData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("File Downloaded");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const expectedColumnTitles = [
    "Company",
    "First Name",
    "Last Name",
    "Job Title",
    "Email",
  ];

  const dataHasExpectedColumns = originalData && originalData.length;

  return (
    <div className="campaign-dash-csv-upload-wrapper">
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <div className="row">
          <div className="dedupe-form-wrapper">
            <div className="form-section">
              <div className="campaign-dash-csv-upload-main">
                <div className="events-owners-subheading">
                  Upload contacts CSV to Salesforce Campaign
                </div>

                {/* Show "Load contacts from CSV" ONLY when no file is selected */}
                {(!csvData || csvData.length === 0) && (
                  <button
                    className="upload-csv-btn btn btn-sm btn-primary"
                    onClick={handleCsvUploadBtnClick}
                  >
                    {`Load contacts from CSV`}
                    <i className="event-owner-add fa-sharp fa-solid fa-upload ml-05"></i>
                  </button>
                )}

                <input
                  type="file"
                  accept=".csv"
                  id="csv-file-input"
                  ref={csvInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                {/* When a CSV is present, show name + Remove button */}
                {csvData && csvData.length > 0 ? (
                  <div className="mt-2">
                    <div className="file-name file-name-with-action">
                      <span className="file-name__text">{csvFileName}</span>
                      <button
                        type="button"
                        className="btn btn-xs btn-danger remove-file-btn"
                        onClick={handleRemoveFile}
                        title="Remove file"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="file-name">
                      Contacts found: {addCommas(originalData.length)}
                    </div>
                    <div className="file-name sm">
                      {`Parsed rows: ${addCommas(csvDiagnostics.parsedRows)} | Non-empty rows: ${addCommas(csvDiagnostics.nonEmptyRows)} | Parse warnings: ${addCommas(csvDiagnostics.parseWarnings)}`}
                    </div>
                    <div className="file-name sm">
                      {`Detected list source: ${detectedListSource || "Unknown"}`}
                    </div>

                    <div className="filter-option mt-4">
                      <div className="input-group">
                        <span className="input-group-text">
                          <div className="option-title">Set contact status: </div>{" "}
                        </span>
                        <select
                          className="form-control rmono"
                          value={status || "Unsent"}
                          onChange={handleUploadStatusChange}
                        >
                          {StatusOptions.map(c => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="example-text-alt">
                      For this campaign, the recommended default status is{" "}'{getRecommendedStatusForCampaignType(selectedCampaign?.RecordType?.Name)}'
                    </div>
                    <div className="campaign-upload-advanced mt-2">
                      <label className="campaign-upload-checkbox">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={skipExistingCampaignMembers}
                          onChange={(e) => setSkipExistingCampaignMembers(Boolean(e.target.checked))}
                        />
                        <span>Skip existing campaign members (match by email)</span>
                      </label>
                      <div className="campaign-upload-checkbox-help">
                        Existing campaign member emails are skipped so only new contacts are added.
                      </div>
                      <label className="campaign-upload-checkbox mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={runContactsThroughMillionVerifier}
                          onChange={(e) =>
                            setRunContactsThroughMillionVerifier(Boolean(e.target.checked))
                          }
                        />
                        <span>Run contacts through MillionVerifier</span>
                      </label>
                      <div className="campaign-upload-checkbox-help">
                        Verifies new contact emails before upload and removes addresses that are likely invalid or likely to bounce.
                      </div>
                      <div className="campaign-upload-comment">
                        <label className="campaign-upload-label">
                          Campaign member comment (applied to all inserted members)
                        </label>
                        <textarea
                          className="form-control rmono campaign-upload-comment-input"
                          value={campaignMemberComment}
                          onChange={(e) => setCampaignMemberComment(e.target.value)}
                          placeholder="Optional comment for all inserted campaign members"
                          rows={2}
                        />
                      </div>
                    </div>
                    {/* Upload to Salesforce button appears after CSV is uploaded */}
                    <div className="mt-4">
                      <button
                        className="upload-csv-btn btn btn-sm btn-primary"
                        onClick={uploadContactsToSalesforce}
                        disabled={uploadingToSf || streaming || !dataHasExpectedColumns}
                        title={
                          dataHasExpectedColumns
                            ? (streaming ? "Receiving progress…" : "Upload contacts to Salesforce Campaign")
                            : "CSV columns do not match expected format"
                        }
                      >
                        {uploadingToSf
                          ? "Starting…"
                          : streaming
                          ? "Uploading…"
                          : "Upload contacts to Salesforce Campaign"}
                        <i className="event-owner-add fa-sharp fa-solid fa-upload ml-05"></i>
                      </button>
                      {!dataHasExpectedColumns && (
                        <div className="file-name sm mt-1">
                          Data not formatted correctly ❌<br />
                          CSV must have: {expectedColumnTitles.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {csvData && csvData.length > 0 ? (
                <div className="mt-2">
                  <div className="sse-log mt-3">
                    <div className="sse-controls">
                      {/* <label className="sse-autoscroll">
                        <input
                          type="checkbox"
                          checked={autoScroll}
                          onChange={(e) => setAutoScroll(e.target.checked)}
                        />
                        Auto-scroll
                      </label> */}
                      <div className="sse-spacer" />
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(logLines.join("\n"));
                            toast.success("Logs copied");
                          } catch {
                            toast.error("Copy failed");
                          }
                        }}
                      >
                        Copy logs
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline ml-1"
                        onClick={() => setLogLines([])}
                      >
                        Clear
                      </button>
                    </div>

                    <textarea
                      ref={logRef}
                      className="sse-log-textarea"
                      readOnly
                      value={logLines.join("\n")}
                      placeholder="Progress messages will appear here…"
                    />
                    <div className="sse-status">
                      Status: {streaming ? "receiving updates…" : "idle"}
                    </div>
                    {uploadSummary ? (
                      <div className="sse-status mt-1">
                        <div>
                          <strong>Upload breakdown</strong>
                        </div>
                        <div>
                          {`Input rows: ${addCommas(uploadSummary?.metrics?.input || 0)}`}
                        </div>
                        {(uploadSummary?.metrics?.skippedExistingCampaignMemberRows || 0) > 0 ? (
                          <div>
                            {`Skipped existing campaign member emails: ${addCommas(
                              uploadSummary?.metrics?.skippedExistingCampaignMemberRows || 0
                            )} (from original input ${addCommas(
                              uploadSummary?.metrics?.originalInput ||
                                uploadSummary?.metrics?.input ||
                                0
                            )})`}
                          </div>
                        ) : null}
                        {uploadSummary?.metrics?.millionVerifierEnabled ? (
                          <div>
                            {`MillionVerifier: checked ${addCommas(
                              uploadSummary?.metrics?.millionVerifierCandidates || 0
                            )} email(s) | cache hits ${addCommas(
                              uploadSummary?.metrics?.millionVerifierCacheHits || 0
                            )} | API checks ${addCommas(
                              uploadSummary?.metrics?.millionVerifierSubmitted || 0
                            )} | filtered out ${addCommas(
                              uploadSummary?.metrics?.millionVerifierFilteredOut || 0
                            )} likely bad/bounce emails | allowed ${addCommas(
                              uploadSummary?.metrics?.millionVerifierPassed || 0
                            )}`}
                          </div>
                        ) : null}
                        <div>
                          {`Resolved rows: ${addCommas(uploadSummary?.metrics?.resolvedRows || 0)} | Unique resolved contacts: ${addCommas(uploadSummary?.metrics?.resolvedUniqueIds || 0)}`}
                        </div>
                        <div>
                          {`Rows merged into existing contacts: ${addCommas(uploadSummary?.metrics?.collapsedDuplicateRows || 0)} | Duplicate probe matches: ${addCommas(uploadSummary?.counts?.duplicateInsights || 0)}`}
                        </div>
                        <div>
                          {`Linked campaign members: ${addCommas(uploadSummary?.counts?.linkedMembers || 0)} | Attached this run: ${addCommas(uploadSummary?.counts?.attachedMembers || 0)} | Updated this run: ${addCommas(uploadSummary?.counts?.updatedMembers || 0)}`}
                        </div>
                        <div>
                          {`Created contacts: ${addCommas(uploadSummary?.counts?.createdContacts || 0)} | Failed creates: ${addCommas(uploadSummary?.counts?.failedCreateContacts || 0)} | Unresolved contacts: ${addCommas(uploadSummary?.counts?.unresolvedContacts || 0)}`}
                        </div>
                        <div>
                          {`Flow: input ${addCommas(uploadSummary?.metrics?.input || 0)} -> resolved rows ${addCommas(uploadSummary?.metrics?.resolvedRows || 0)} -> unique contacts ${addCommas(uploadSummary?.metrics?.resolvedUniqueIds || 0)} -> linked ${addCommas(uploadSummary?.metrics?.linked || 0)} -> unresolved ${addCommas(uploadSummary?.metrics?.unresolved || 0)}`}
                        </div>
                        <div>
                          Merged rows are already represented in linked unique contacts.
                        </div>
                        {Array.isArray(uploadSummary?.failedCreateContacts) &&
                        uploadSummary.failedCreateContacts.length > 0 ? (
                          <div className="mt-1">
                            <strong>Failed create samples:</strong>{" "}
                            {uploadSummary.failedCreateContacts
                              .slice(0, 5)
                              .map((c) => c.email || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "unknown")
                              .join(", ")}
                            {uploadSummary?.truncated?.failedCreateContacts ? " ..." : ""}
                          </div>
                        ) : null}
                        {Array.isArray(uploadSummary?.unresolvedContacts) &&
                        uploadSummary.unresolvedContacts.length > 0 ? (
                          <div className="mt-1">
                            <strong>Unresolved samples:</strong>{" "}
                            {uploadSummary.unresolvedContacts
                              .slice(0, 5)
                              .map((c) => c.email || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "unknown")
                              .join(", ")}
                            {uploadSummary?.truncated?.unresolvedContacts ? " ..." : ""}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="sse-status mt-2">
                      <div>
                        <strong>Recent upload history</strong>
                      </div>
                      {loadingUploadHistory ? (
                        <div>Loading history...</div>
                      ) : uploadHistory.length ? (
                        <div>
                          {uploadHistory.slice(0, 5).map((row, idx) => {
                            const finishedAt = row?.finishedAt || row?.at || "";
                            const tsLabel = finishedAt
                              ? new Date(finishedAt).toLocaleString()
                              : "unknown time";
                            const m = row?.summary?.metrics || row?.summary?.summary?.metrics || row?.summary || {};
                            const input = Number(m?.input || m?.inputContacts || 0);
                            const resolvedRows = Number(m?.resolvedRows || m?.resolvedInputRows || 0);
                            const collapsed = Number(m?.collapsedDuplicateRows || m?.duplicateInputRowsCollapsed || 0);
                            const unresolved = Number(m?.unresolved || m?.unresolvedContacts || 0);
                            const state = row?.state || "unknown";
                            return (
                              <div key={`${row?.jobId || "job"}-${idx}`}>
                                {`${tsLabel} | ${state} | input ${addCommas(input)} | resolved rows ${addCommas(resolvedRows)} | collapsed ${addCommas(collapsed)} | unresolved ${addCommas(unresolved)}`}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div>No upload history yet for this campaign.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hidden master CSV controls kept intact (if you still use them) */}
      <input
        type="file"
        accept=".csv"
        id="master-csv-file-input"
        ref={masterCsvInputRef}
        style={{ display: "none" }}
        onChange={handleMasterFileUpload}
      />
    </div>
  );
};

const Dashboard = (props) => {
  useDocumentTitle("Campaign Dashboard - Landmark Ventures");

  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [salesforceUsers, setSalesforceUsers] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingSelectedCampaign, setLoadingSelectedCampaign] = useState(false);
  const [refreshingCampaignContacts, setRefreshingCampaignContacts] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [socket, setSocket] = useState(null);
  const [emailJobs, setEmailJobs] = useState([]);
  const [triggerSendEmailsRefresh, setTriggerSentEmailsRefresh] = useState(false);

  // Light theme once
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Touch lastSeenTime when authenticated (fire & forget)
  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get("landmark/employees/login");
    } catch (err) {
      console.warn("employee login touch failed", err);
    }
  }, [isAuthenticated, employee]);

  function createWebsocket() {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const WS_URL_PROD = "wss://betterblocks.landmarkventures.com:7769";
    const WS_URL = "wss://localhost:7769";
    const websocketUrl = isLocalhost ? WS_URL : WS_URL_PROD;
    const ws = new WebSocket(websocketUrl);

    ws.onopen = () => {
      console.log("WebSocket connection opened");
      // authenticate via employee session/identity (no employee)
      if (isAuthenticated && employee) {
        authenticateWebSocket(ws);
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data) {
        if (data.triggerEmailRefresh) {
          setTriggerSentEmailsRefresh(true);
          setTimeout(() => {
            setEmailJobs([]); // when multiple jobs running this will bug (kept as-is)
          }, 2000);
          toast.success("Email Sending Completed");
        } else if (data.emailJobs) {
          setEmailJobs(data.emailJobs);
          console.log(`emailJobs`, data.emailJobs);
        }
      }
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
    };

    setSocket(ws);

    // Note: original code returned a cleanup but didn't use it; keeping behavior unchanged
    return () => {
      if (ws) ws.close();
    };
  }

  // send employee-based auth payload (server should validate session cookie or employeeId)
  const authenticateWebSocket = (ws) => {
    if (ws && ws.readyState === WebSocket.OPEN && employee) {
      const message = JSON.stringify({ employeeId: employee.employeeId });
      ws.send(message);
      console.log("authenticateWebSocket() sent employeeId");
    }
  };

  useEffect(() => {
    if (isAuthenticated && employee && !socket) {
      //createWebsocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, employee]);

  useEffect(() => {
    if (triggerSendEmailsRefresh) {
      getSentEmails();
      setTriggerSentEmailsRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSendEmailsRefresh]);

const hasFetchedCampaigns = React.useRef(false);
const getCampaignsInFlight = React.useRef(false);
const latestCampaignFetchTokenRef = React.useRef(0);
const refreshCampaignContactsInFlightRef = React.useRef(0);

async function getCampaigns() {
  if (getCampaignsInFlight.current) return;   // debounce duplicates
  getCampaignsInFlight.current = true;

  console.log(`getCampaigns() - starting`);
  try {
    setLoadingCampaigns(true);
    const api = new ApiService({ employee });
    const [campaignsResult, usersResult] = await Promise.allSettled([
      api.get(`landmark/salesforce/getCampaigns`, null, null, { timeout: 0 }),
      api.get(`landmark/salesforce/getSalesforceUsers`, null, null, { timeout: 0 }),
    ]);
    if (campaignsResult.status === "fulfilled") {
      setCampaigns(campaignsResult.value);
      console.log(`getCampaigns() - finished`, campaignsResult.value);
    } else {
      console.error(`getCampaigns() - error`, campaignsResult.reason);
    }
    if (usersResult.status === "fulfilled") {
      const users = Array.isArray(usersResult.value) ? usersResult.value : [];
      setSalesforceUsers(users);
      console.log(`getSalesforceUsers() - finished`, users.length);
    } else {
      console.error(`getSalesforceUsers() - error`, usersResult.reason);
      setSalesforceUsers([]);
    }
  } catch (error) {
    console.error(`getCampaigns() - error`, error);
  } finally {
    setLoadingCampaigns(false);
    getCampaignsInFlight.current = false;
  }
}

  async function getSentEmails() {
    if (!selectedCampaign) {
      console.log(`getSentEmails() - Error: selectedCampaign is null`);
      return;
    }
    try {
      const api = new ApiService({ employee });
      const response = await api.get(
        `landmark/campaigns/getSentEmailsForCampaign/${selectedCampaign.campaignId}`, null, null, { timeout: 0 });
      setSentEmails(response);
    } catch (error) {
      console.error(error);
    }
  }

  async function getCampaignWithMembers(campaignId, { silent = false, showContactsRefresh = false } = {}) {
    const _campaignId = String(campaignId || selectedCampaignId || "").trim();
    const shouldShowContactsRefresh = Boolean(showContactsRefresh);
    if (shouldShowContactsRefresh) {
      refreshCampaignContactsInFlightRef.current += 1;
      setRefreshingCampaignContacts(true);
    }
    console.log(`getCampaignWithMembers() - campaignId: ${_campaignId} - starting`);
    try {
      if(!_campaignId) {
        console.log(`getCampaignWithMembers() - campaignId is null - aborting`);
        return;
      }
      const fetchToken = ++latestCampaignFetchTokenRef.current;
      if (!silent) setLoadingSelectedCampaign(true);
      const api = new ApiService({ employee });
      const withMembers = true;
      const response = await api.get(`landmark/salesforce/getCampaign/${_campaignId}/${withMembers}`, null, null, { timeout: 0 });
      // Ignore stale responses when users switch campaigns quickly.
      if (fetchToken !== latestCampaignFetchTokenRef.current) {
        console.log(
          `getCampaignWithMembers() - stale response ignored for campaignId=${_campaignId}`
        );
        return;
      }
      setSelectedCampaign(response);
    console.log(`getCampaignWithMembers() - campaignId: ${_campaignId} - finished`, response);

    } catch (error) {
    console.error(`getCampaignWithMembers() - campaignId: ${_campaignId} - error`, error);
    } finally {
      if (!silent) setLoadingSelectedCampaign(false);
      if (shouldShowContactsRefresh) {
        refreshCampaignContactsInFlightRef.current = Math.max(
          0,
          refreshCampaignContactsInFlightRef.current - 1
        );
        if (refreshCampaignContactsInFlightRef.current === 0) {
          setRefreshingCampaignContacts(false);
        }
      }
    }
  }

  async function updateCampaignMemberStatus(status, campaignMemberId) {
    try {
      if(!status || !campaignMemberId) {
        console.log(`updateCampaignMemberStatus() - missing status or campaignMemberId`);
        return;
      }
      // The status dropdown is populated from the campaign's own
      // CampaignMemberStatus list, so we no longer validate against a hardcoded
      // option list here (which would reject valid BD statuses like "Sent").
      // Salesforce validates the status on update.
      const api = new ApiService({ employee });
      const url = "landmark/salesforce/updateCampaignMemberStatus";
      const response = await api.post(url, { campaignMemberId, status });
      console.log(`updateCampaignMemberStatus() - response`, response);
      const {success, error, updatedMember} = response || {};
      // update selectedCampaign members
      if(success && updatedMember && updatedMember.Status === status && selectedCampaign && selectedCampaign.members) {
        const updatedMembers = selectedCampaign.members.map((m) => {
          if(m.Id === campaignMemberId) {
            return { ...m, Status: status  };
          }
          return m;
        });
        setSelectedCampaign({ ...selectedCampaign, members: updatedMembers });
        toast.success(`Updated ${updatedMember.Name} to status: ${updatedMember.Status}`);
      }
      else {
        console.log(`updateCampaignMemberStatus() - failed to update status`);
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function updateCampaignMemberComment(comment, campaignMemberId) {
    try {
      if (!campaignMemberId) {
        console.log(`updateCampaignMemberComment() - missing campaignMemberId`);
        return false;
      }
      const normalizedComment = String(comment ?? "");
      const api = new ApiService({ employee });
      const url = "landmark/salesforce/updateCampaignMemberComment";
      const response = await api.post(url, {
        campaignMemberId,
        comment: normalizedComment,
      });
      const { success, updatedMember } = response || {};
      if (success && updatedMember) {
        if (selectedCampaign && selectedCampaign.members) {
          const updatedMembers = selectedCampaign.members.map((m) =>
            m.Id === campaignMemberId
              ? { ...m, Comments__c: normalizedComment }
              : m
          );
          setSelectedCampaign({ ...selectedCampaign, members: updatedMembers });
        }
        toast.success("Updated campaign member comment");
        return true;
      }
      console.log(`updateCampaignMemberComment() - failed to update comment`);
      toast.error("Failed to update comment");
      return false;
    } catch (error) {
      console.error(error);
      toast.error("Failed to update comment");
      return false;
    }
  }

  async function bulkUpdateCampaignMembers(
    campaignMemberIds,
    {
      status,
      comment,
      updateLastSent,
      emailVersion,
      campaignSentAs,
      lastSent,
    } = {}
  ) {
    try {
      const ids = [
        ...new Set(
          (Array.isArray(campaignMemberIds) ? campaignMemberIds : [])
            .map((id) => String(id || "").trim())
            .filter(Boolean)
        ),
      ];
      const hasStatus = typeof status === "string" && status.trim().length > 0;
      const hasComment = typeof comment === "string";
      const hasEmailVersion = typeof emailVersion === "string";
      const hasCampaignSentAs =
        typeof campaignSentAs === "string" && campaignSentAs.trim().length > 0;
      const hasExplicitLastSent =
        typeof lastSent === "string" && lastSent.trim().length > 0;
      if (!ids.length) {
        toast.error("No members selected");
        return false;
      }
      if (
        !hasStatus &&
        !hasComment &&
        !hasEmailVersion &&
        !hasCampaignSentAs &&
        !hasExplicitLastSent &&
        !updateLastSent
      ) {
        return false;
      }

      const api = new ApiService({ employee });
      const url = "landmark/salesforce/bulkUpdateCampaignMembers";
      const payload = { campaignMemberIds: ids };
      if (hasStatus) payload.status = status;
      if (hasComment) payload.comment = comment;
      if (hasEmailVersion) payload.emailVersion = emailVersion;
      if (hasCampaignSentAs) payload.campaignSentAs = campaignSentAs;
      if (hasExplicitLastSent) payload.lastSent = lastSent;
      if (updateLastSent && hasStatus && !hasExplicitLastSent) {
        payload.updateLastSent = true;
      }
      const response = await api.post(url, payload, null, { timeout: 0 });
      const { success, updatedCount, failedCount, lastSentValue } = response || {};
      if (success) {
        if (selectedCampaign && selectedCampaign.members) {
          const idSet = new Set(ids);
          const stampedLastSent =
            lastSentValue ||
            (hasExplicitLastSent
              ? lastSent
              : updateLastSent && hasStatus
              ? new Date().toISOString()
              : null);
          const updatedMembers = selectedCampaign.members.map((m) => {
            if (!idSet.has(m.Id)) return m;
            const next = { ...m };
            if (hasStatus) next.Status = status;
            if (hasComment) next.Comments__c = String(comment ?? "");
            if (hasEmailVersion) {
              next.Email_Version__c = String(emailVersion ?? "");
            }
            if (hasCampaignSentAs) {
              next.Campaign_Sent_As__c = String(campaignSentAs || "").trim();
            }
            if (stampedLastSent) {
              next.Last_Sent__c = stampedLastSent;
              next.Outreach_Counter__c =
                (Number(m.Outreach_Counter__c) || 0) + 1;
            }
            return next;
          });
          setSelectedCampaign({ ...selectedCampaign, members: updatedMembers });
        }
        toast.success(
          `Updated ${updatedCount ?? ids.length} member${
            (updatedCount ?? ids.length) === 1 ? "" : "s"
          }${failedCount ? `, ${failedCount} failed` : ""}`
        );
        return true;
      }
      toast.error("Bulk update failed");
      return false;
    } catch (error) {
      console.error(error);
      toast.error("Bulk update failed");
      return false;
    }
  }

  async function startDeleteCampaignMembersJob(campaignMemberIds = []) {
    try {
      const normalizedIds = [...new Set(
        (Array.isArray(campaignMemberIds) ? campaignMemberIds : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      )];
      if (!normalizedIds.length) {
        return {
          success: false,
          deletedCampaignMemberIds: [],
          failed: [],
        };
      }
      const api = new ApiService({ employee });
      return await api.post(
        "landmark/salesforce/deleteCampaignMembers",
        { campaignMemberIds: normalizedIds },
        null,
        { timeout: 0 }
      );
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: error?.message || "Failed to start delete campaign members job",
      };
    }
  }

  function applyDeletedCampaignMembers(deletedCampaignMemberIds = []) {
    const normalizedIds = [...new Set(
      (Array.isArray(deletedCampaignMemberIds) ? deletedCampaignMemberIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )];
    if (!normalizedIds.length) return;
    const deletedIdSet = new Set(normalizedIds);
    setSelectedCampaign((prevCampaign) => {
      if (!prevCampaign || !Array.isArray(prevCampaign.members)) {
        return prevCampaign;
      }
      return {
        ...prevCampaign,
        members: prevCampaign.members.filter(
          (member) => !deletedIdSet.has(member?.Id)
        ),
      };
    });
  }

  // initial loads after auth
useEffect(() => {
  if (!isAuthenticated || hasFetchedCampaigns.current) return;
  hasFetchedCampaigns.current = true;
  let cancelled = false;

  (async () => {
    await getCampaigns();
  })();

  return () => { cancelled = true; };
}, [isAuthenticated]);

  useEffect(() => {
    if (selectedCampaignId) {
      getCampaignWithMembers(selectedCampaignId);
    }
    else {
      setSelectedCampaign(null);
    }
  }, [selectedCampaignId]);

  async function refreshSelectedCampaign(options = {}) {
   if(selectedCampaignId) {
    getCampaignWithMembers(selectedCampaignId, options);
   }
  }

  async function editCampaign(campaign) {
    try {
      const editedCampaign = { ...campaign, contacts: undefined };
      const api = new ApiService({ employee });
      const url = "landmark/campaigns/editCampaign";
      const response = await api.post(url, { campaign: editedCampaign }, null, null, { timeout: 0 });
      // refresh campaigns
      refreshSelectedCampaign();
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function handleSetSelectedEmailTemplate(template) {
    if (template && selectedCampaign) {
      selectedCampaign.currentEmailTemplateId = template.templateId;
      setSelectedEmailTemplate(template);
      const response = await editCampaign(selectedCampaign);
      const { success, error } = response || {};
      if (!success) {
        toast.error("An error occurred");
      } else if (error) {
        toast.error(error);
      } else {
        toast.success("Campaign Email Template Updated");
      }
    }
  }

  // Show a simple spinner while AuthContext initializes
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "Campaign Uploader", url: `/landmark/campaign/uploader` },
          ]}
        />

        {isAuthenticated ? (
          <>
            {/* <AddEditCampaign
              campaigns={campaigns}
              getCampaigns={getCampaigns}
              employee={employee}
            /> */}
            <SelectCampaign
              employee={employee}
              campaigns={campaigns}
              salesforceUsers={salesforceUsers}
              selectedCampaignId={selectedCampaignId}
              setSelectedCampaignId={setSelectedCampaignId}
              selectedCampaign={selectedCampaign}
              loading={loadingCampaigns}
              loadingSelectedCampaign={loadingSelectedCampaign}
              refreshSelectedCampaign={refreshSelectedCampaign}
            />
            {/* {selectedCampaign && (
              <SelectTasks
                selectedCampaign={selectedCampaign}
                employee={employee}
                refreshCampaign={refreshCampaign}
              />
            )}
            {selectedCampaign && (
              <SelectTemplate
                templates={templates}
                getTemplates={getTemplates}
                selectedCampaign={selectedCampaign}
                selectedEmailTemplate={selectedEmailTemplate}
                setSelectedEmailTemplate={handleSetSelectedEmailTemplate}
                employee={employee}
              />
            )} */}
            
            {!loadingSelectedCampaign && selectedCampaign && (
              <MasterEventsTable
                updateCampaignMemberComment={updateCampaignMemberComment}
                bulkUpdateCampaignMembers={bulkUpdateCampaignMembers}
                selectedCampaign={selectedCampaign}
                employee={employee}
                updateCampaignMemberStatus={updateCampaignMemberStatus}
                startDeleteCampaignMembersJob={startDeleteCampaignMembersJob}
                applyDeletedCampaignMembers={applyDeletedCampaignMembers}
                campaigns={campaigns}
                salesforceUsers={salesforceUsers}
                sentEmails={sentEmails}
                getSentEmails={getSentEmails}
                emailJobs={emailJobs}
                refreshSelectedCampaign={refreshSelectedCampaign}
                loadingContactsRefresh={refreshingCampaignContacts}
              />
            )}
            {!loadingSelectedCampaign && (
              <CampaignUploadMetricsPanel
                employee={employee}
                selectedCampaign={selectedCampaign}
              />
            )}
          </>
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the dashboard.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Dashboard;
